import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, 
  ShoppingBag, 
  Settings, 
  Plus, 
  Edit3, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  LogOut, 
  TrendingUp, 
  MessageCircle, 
  Search, 
  DollarSign, 
  Truck, 
  ShieldCheck, 
  ArrowUpRight,
  Download,
  RotateCcw,
  User,
  Phone,
  FileText,
  HelpCircle,
  Sparkles,
  Layout,
  Layers,
  HeartPulse,
  Share2,
  Type,
  Image as ImageIcon,
  FolderTree,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  Upload,
  FileUp,
  FileDown,
  Mail,
  MapPin,
  Star,
  MessageSquare,
  Check,
  CheckCircle,
  Banknote,
  X,
  LayoutDashboard,
  Menu,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  Clock,
  Home,
  RefreshCw,
  Copy,
  QrCode,
  Smartphone,
  Shield
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { DEFAULT_CMS_CONTENT } from '../../data/defaultCmsData';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import ProductFormModal from './ProductFormModal';
import CategoryManager from './CategoryManager';
import ImageUploadField from './ImageUploadField';
import BlogManager from './BlogManager';
import PageManager from './PageManager';
import MediaManager from './MediaManager';
import DashboardOverview from './DashboardOverview';

const AdminDashboard = ({ onBackToStore }) => {
  const { lang, t } = useLanguage();
  const { 
    settings, 
    products, 
    orders, 
    cmsContent,
    categories,
    ailments,
    mizajList,
    addProduct, 
    updateProduct, 
    deleteProduct, 
    toggleStock, 
    updateOrderStatus, 
    deleteOrder, 
    updateSettings, 
    updateCmsContent,
    resetCmsToDefaults,
    resetToDefaults,
    reviews,
    addReview,
    updateReviewStatus,
    deleteReview,
    blogs,
    exportFullBackup,
    importFullBackup,
    downloadProductsJsFile,
    getProductsJsCode,
    downloadCategoriesJsFile,
    getCategoriesJsCode,
    serverSyncStatus,
    logoutAdmin 
  } = useStore();

  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'orders', 'products', 'categories', 'reviews', 'blogs', 'pages', 'media', 'settings', 'cms', 'backup'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const [activeCmsSection, setActiveCmsSection] = useState('hero'); // 'hero', 'announcement', 'hakim', 'footer', 'badges', 'faqs'
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productStockFilter, setProductStockFilter] = useState('All'); // 'All', 'InStock', 'OutOfStock'
  const [orderStatusFilter, setOrderStatusFilter] = useState('All');

  // Backup & Code export copy states
  const [copiedProductCode, setCopiedProductCode] = useState(false);
  const [copiedCatCode, setCopiedCatCode] = useState(false);

  // Reviews states
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewFilter, setReviewFilter] = useState('All');
  const [adminAddReviewModal, setAdminAddReviewModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState(null);
  const [newReviewForm, setNewReviewForm] = useState({
    productId: '',
    customerName: '',
    city: '',
    orderId: '',
    rating: 5,
    comment: ''
  });

  // Backup file ref & status
  const backupFileInputRef = useRef(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [showAdminPin, setShowAdminPin] = useState(false);
  const [pinChangeMsg, setPinChangeMsg] = useState(null);
  const [viewingReceiptModal, setViewingReceiptModal] = useState(null);

  // Settings local form state
  const [settingsForm, setSettingsForm] = useState(settings);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [newAdminEmailInput, setNewAdminEmailInput] = useState('');

  // Admin Emails Helpers
  const getAdminEmailList = () => {
    if (Array.isArray(settingsForm.adminEmails) && settingsForm.adminEmails.length > 0) {
      return settingsForm.adminEmails;
    }
    if (typeof settingsForm.adminEmails === 'string' && settingsForm.adminEmails.trim()) {
      return settingsForm.adminEmails.split(',').map(e => e.trim()).filter(Boolean);
    }
    if (settingsForm.email && settingsForm.email.trim()) {
      return [settingsForm.email.trim()];
    }
    return ['sherazi313@gmail.com'];
  };

  const handleAddAdminEmail = () => {
    const clean = (newAdminEmailInput || '').trim().toLowerCase();
    if (!clean || !clean.includes('@') || !clean.includes('.')) {
      alert('براہ کرم درست ای میل ایڈریس درج کریں۔');
      return;
    }
    const current = getAdminEmailList();
    if (current.includes(clean)) {
      alert('یہ ای میل پہلے سے موجود ہے۔');
      return;
    }
    const updated = [...current, clean];
    setSettingsForm({ ...settingsForm, adminEmails: updated, email: updated[0] });
    setNewAdminEmailInput('');
  };

  const handleRemoveAdminEmail = (emailToRemove) => {
    const current = getAdminEmailList();
    if (current.length <= 1) {
      alert('کم از کم ایک ایڈمن ریکوری ای میل کا ہونا لازمی ہے۔');
      return;
    }
    const updated = current.filter(e => e !== emailToRemove);
    setSettingsForm({ ...settingsForm, adminEmails: updated, email: updated[0] });
  };

  // 2FA and Security State
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [copiedUrlToast, setCopiedUrlToast] = useState(false);
  const [revokingDevices, setRevokingDevices] = useState(false);
  const [revokedMessage, setRevokedMessage] = useState(null);

  const generateRandomBase32 = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleGenerateNewTotpSecret = () => {
    const newSecret = generateRandomBase32();
    const storeTitle = 'HimalayanPansar';
    const user = settingsForm.adminUsername || 'admin';
    const otpAuthUrl = `otpauth://totp/${storeTitle}:${user}?secret=${newSecret}&issuer=${storeTitle}&period=30&digits=6`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(otpAuthUrl)}`;
    
    setTotpSetupData({
      totpSecret: newSecret,
      otpAuthUrl,
      qr_url: qrUrl,
      qrUrl,
      status: 'success'
    });
    setSettingsForm(prev => ({ ...prev, totpSecret: newSecret }));
  };

  const handleFetchTotpSetup = async (syncFormState = false) => {
    const currentSecret = settingsForm.totpSecret || generateRandomBase32();
    const storeTitle = 'HimalayanPansar';
    const user = settingsForm.adminUsername || 'admin';
    const fallbackOtpAuthUrl = `otpauth://totp/${storeTitle}:${user}?secret=${currentSecret}&issuer=${storeTitle}&period=30&digits=6`;
    const fallbackQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(fallbackOtpAuthUrl)}`;

    setTotpSetupData(prev => prev || {
      totpSecret: currentSecret,
      otpAuthUrl: fallbackOtpAuthUrl,
      qr_url: fallbackQrUrl,
      qrUrl: fallbackQrUrl,
      status: 'success'
    });

    if (!settingsForm.totpSecret) {
      setSettingsForm(prev => ({ ...prev, totpSecret: currentSecret }));
    }

    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const isSubfolder = typeof window !== 'undefined' && window.location.pathname.includes('/himalayan');
      const authUrl = `${origin}${isSubfolder ? '/himalayan/api/auth.php?action=get_2fa_setup' : '/api/auth.php?action=get_2fa_setup'}`;
      const token = localStorage.getItem('himalayan_admin_token');
      const res = await fetch(authUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}`, 'X-Admin-Token': token } : {}
      });
      const data = await res.json();
      if (data && data.status === 'success') {
        const qr = data.qr_url || data.qrUrl || fallbackQrUrl;
        setTotpSetupData({ ...data, qr_url: qr, qrUrl: qr });
        setSettingsForm(prev => {
          if (syncFormState) {
            return {
              ...prev,
              adminUsername: data.adminUsername || prev.adminUsername || 'admin',
              totpSecret: data.totpSecret || prev.totpSecret || currentSecret,
              twoFactorType: data.twoFactorType || prev.twoFactorType || 'disabled',
              adminSecretSlug: data.adminSecretSlug || prev.adminSecretSlug || 'sherazi-5083',
              adminEmails: (Array.isArray(data.adminEmails) && data.adminEmails.length > 0) ? data.adminEmails : prev.adminEmails
            };
          } else {
            return {
              ...prev,
              totpSecret: prev.totpSecret || data.totpSecret || currentSecret
            };
          }
        });
      }
    } catch (e) {}
  };

  // Automatically fetch fresh security & 2FA details on initial mount
  useEffect(() => {
    handleFetchTotpSetup(true);
  }, []);

  const handleRevokeAllDevices = async () => {
    if (!window.confirm('کیا آپ واقعی تمام ٹرسٹڈ ڈیوائسز کی توثیق ختم کرنا چاہتے ہیں؟ اگلی بار لاگ ان پر 2FA کوڈ دوبارہ مانگا جائے گا۔')) return;
    setRevokingDevices(true);
    setRevokedMessage(null);
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const isSubfolder = typeof window !== 'undefined' && window.location.pathname.includes('/himalayan');
      const authUrl = `${origin}${isSubfolder ? '/himalayan/api/auth.php?action=revoke_devices' : '/api/auth.php?action=revoke_devices'}`;
      const token = localStorage.getItem('himalayan_admin_token');
      const res = await fetch(authUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await res.json();
      setRevokedMessage(data?.message || 'تمام ٹرسٹڈ ڈیوائسز منسوخ کر دی گئیں۔');
      setTimeout(() => setRevokedMessage(null), 4000);
    } catch (e) {
      setRevokedMessage('سرور سے رابطہ نہ ہو سکا۔');
    } finally {
      setRevokingDevices(false);
    }
  };

  // CMS Content local form state
  const [cmsForm, setCmsForm] = useState(cmsContent);
  const [cmsSaved, setCmsSaved] = useState(false);

  useEffect(() => {
    setSettingsForm(settings);
  }, [settings]);

  useEffect(() => {
    setCmsForm(cmsContent);
  }, [cmsContent]);

  // CMS Helpers
  const handleSaveCms = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      updateCmsContent(cmsForm);
      setCmsSaved(true);
      setTimeout(() => setCmsSaved(false), 2500);
    } catch (err) {
      console.error('Error saving CMS content:', err);
      alert('تبدیلیاں محفوظ کرتے وقت مسئلہ پیش آیا۔ براہ کرم تصویر کا سائز چیک کریں اور دوبارہ کوشش کریں۔');
    }
  };

  const handleAddFaq = () => {
    const newFaq = {
      id: `faq-${Date.now()}`,
      qUrdu: 'نیا طبی یا خریداری کا سوال درج کریں',
      qEn: 'Enter new question in English',
      aUrdu: 'اس سوال کا جواب یہاں لکھیں...',
      aEn: 'Answer in English...'
    };
    setCmsForm(prev => ({
      ...prev,
      faqs: [...(prev.faqs || []), newFaq]
    }));
  };

  const handleDeleteFaq = (faqId) => {
    setCmsForm(prev => ({
      ...prev,
      faqs: (prev.faqs || []).filter(f => f.id !== faqId)
    }));
  };

  const handleUpdateFaq = (faqId, field, value) => {
    setCmsForm(prev => ({
      ...prev,
      faqs: (prev.faqs || []).map(f => f.id === faqId ? { ...f, [field]: value } : f)
    }));
  };

  const handleUpdateBadge = (index, field, value) => {
    setCmsForm(prev => {
      const updated = [...(prev.trustBadges || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, trustBadges: updated };
    });
  };

  const handleUpdateArchetype = (index, field, value) => {
    setCmsForm(prev => {
      const archetypes = [...(prev.tibbiDiagnosis?.archetypes || [])];
      archetypes[index] = { ...archetypes[index], [field]: value };
      return {
        ...prev,
        tibbiDiagnosis: {
          ...prev.tibbiDiagnosis,
          archetypes
        }
      };
    });
  };

  // Statistics
  const totalRevenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
  const pendingOrdersCount = orders.filter(o => o.status === 'Pending').length;
  const outOfStockCount = products.filter(p => !p.inStock).length;

  const handleSaveProduct = (productData) => {
    if (editingProduct) {
      updateProduct(productData);
    } else {
      addProduct(productData);
    }
    setEditingProduct(null);
  };

  const handleOpenEditProduct = (prod) => {
    setEditingProduct(prod);
    setProductModalOpen(true);
  };

  const handleSaveSettings = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const token = localStorage.getItem('himalayan_admin_token');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const isSubfolder = typeof window !== 'undefined' && window.location.pathname.includes('/himalayan');
    const authUrl = `${origin}${isSubfolder ? '/himalayan/api/auth.php?action=change_credentials' : '/api/auth.php?action=change_credentials'}`;

    const newPassToSubmit = (settingsForm.newAdminPassword || settingsForm.adminPassword || settingsForm.adminPin || '').trim();

    const payload = {
      username: (settingsForm.adminUsername || 'admin').trim(),
      password: newPassToSubmit, // only non-empty will update server password hash
      adminSecretSlug: (settingsForm.adminSecretSlug || 'sherazi-5083').trim(),
      twoFactorType: settingsForm.twoFactorType || 'disabled',
      adminEmails: getAdminEmailList(),
      totpSecret: settingsForm.totpSecret || ''
    };

    try {
      const res = await fetch(authUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Admin-Token': token || ''
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || (data && data.status === 'error')) {
        alert(data?.message || 'سیٹنگز محفوظ کرنے میں مسئلہ پیش آیا۔ براہ کرم سرور چیک کریں۔');
        return;
      }

      // Update local context
      const updatedForm = {
        ...settingsForm,
        adminUsername: payload.username,
        adminSecretSlug: payload.adminSecretSlug,
        twoFactorType: payload.twoFactorType,
        adminEmails: payload.adminEmails,
        totpSecret: payload.totpSecret,
        newAdminPassword: '',
        adminPassword: '',
        adminPin: ''
      };

      updateSettings(updatedForm);
      setSettingsForm(updatedForm);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch (err) {
      console.warn('Could not sync credentials to server auth API:', err);
      // Fallback update in case of offline dev
      updateSettings(settingsForm);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    }
  };

  const handleBackupFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        const res = importFullBackup(parsed);
        if (res.success) {
          setBackupStatus({ type: 'success', text: 'بیک اپ کامیابی سے بحال (Restore) کر دیا گیا!' });
          setTimeout(() => setBackupStatus(null), 4000);
        } else {
          setBackupStatus({ type: 'error', text: `بیک اپ فائل میں خرابی: ${res.error}` });
        }
      } catch (err) {
        setBackupStatus({ type: 'error', text: 'غلط فائل فارمیٹ! براہ کرم درست JSON بیک اپ فائل منتخب کریں۔' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // WhatsApp Order Status Notification to Customer
  const sendOrderStatusWhatsApp = (order, newStatus) => {
    const cleanPhone = order.phone.replace(/[^0-9]/g, '');
    let msg = '';
    const codDetail = order.paymentMethod === 'cod' && order.advanceAmount
      ? `\nکل بل: Rs. ${order.totalAmount}\nپیشگی ایڈوانس: Rs. ${order.advanceAmount}\nبوقتِ ترسیل بقیہ رقم: Rs. ${order.remainingAmount}`
      : `\nکل رقم: Rs. ${order.totalAmount}`;

    if (newStatus === 'Shipped') {
      msg = `السلام علیکم محترم *${order.customerName}* صاحب! 🌿\n\nآپ کا آرڈر نمبر *#${order.id}* روانہ (Shipped) کر دیا گیا ہے۔ ان شاء اللہ جلد آپ کی دہلیز پر پہنچ جائے گا۔${codDetail}\n\nشکریہ: ${settings.storeNameUrdu} (${settings.phone})`;
    } else if (newStatus === 'Delivered') {
      msg = `السلام علیکم محترم *${order.customerName}* صاحب! 🌿\n\nآپ کا آرڈر نمبر *#${order.id}* بحفاظت ڈیلیور ہو چکا ہے۔ ادویات کے طریقہ استعمال یا کسی طبی رہنمائی کے لیے ہمارے واٹس ایپ پر رابطہ کریں۔\n\nشکریہ: ${settings.storeNameUrdu}`;
    } else {
      msg = `السلام علیکم محترم *${order.customerName}*! آپ کا آرڈر *#${order.id}* موصول ہو چکا ہے اور زیرِ عمل ہے۔${codDetail}\n\nشکریہ: ${settings.storeNameUrdu}`;
    }
    window.open(`https://wa.me/92${cleanPhone.replace(/^0/, '')}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleSelectOrderForReview = (order) => {
    setSelectedOrderForReview(order);
    const firstItemName = order.items?.[0]?.name;
    const matchedProd = products.find(p => p.nameUrdu === firstItemName || p.nameEn === firstItemName);
    
    setNewReviewForm({
      orderId: order.id,
      customerName: order.customerName || '',
      city: order.city || '',
      productId: matchedProd?.id || products[0]?.id || '',
      rating: 5,
      comment: ''
    });
  };

  const sendReviewRequestWhatsApp = (order) => {
    const cleanPhone = (order.phone || '').replace(/[^0-9]/g, '');
    const itemNames = (order.items || []).map(it => it.name).join('، ');
    const storeUrl = typeof window !== 'undefined' ? window.location.origin : 'https://himalayanpansar.com';
    const msg = `السلام علیکم محترم *${order.customerName}* صاحب! 🌿\n\nامید ہے آپ بخیریت ہوں گے۔ ہمالین پنسار سٹور (دواخانہ طب پاکستانی) سے آپ کا آرڈر *#${order.id}* (${itemNames}) آپ کو موصول ہو چکا ہوگا۔\n\nدوا کے استعمال کے بعد آپ کو کیسا افاقہ محسوس ہوا؟ دیگر مریضوں کی رہنمائی اور ہمارے دواخانہ کے معیار کے لیے آپ کی رائے بہت قیمتی ہے۔ برائے مہربانی اپنا قیمتی ریویو اور ریٹنگ اس لنک پر درج فرمائیں:\n🔗 ${storeUrl}\nآپ کا آرڈر نمبر: *${order.id}*\n\nیا آپ یہیں واٹس ایپ پر اپنا پیغام یا وائس نوٹ بھیج دیں، ہم آپ کی رائے ویب سائٹ پر درج کر دیں گے۔\nجزاک اللہ خیراً!\nحکیم مولانا سید عبدالوہاب شاہ شیرازی\nدواخانہ طب پاکستانی - ہمالین پنسار`;
    window.open(`https://wa.me/92${cleanPhone.replace(/^0/, '')}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const filteredProducts = products.filter(p => {
    const matchSearch = (p.nameUrdu || '').toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.nameEn || '').toLowerCase().includes(productSearch.toLowerCase());
    const matchStock = productStockFilter === 'All'
      ? true
      : productStockFilter === 'InStock'
      ? p.inStock
      : !p.inStock;
    return matchSearch && matchStock;
  });

  const filteredOrders = orderStatusFilter === 'All' 
    ? orders 
    : orders.filter(o => o.status === orderStatusFilter);

  const filteredReviews = (reviews || []).filter(rev => {
    const matchFilter = reviewFilter === 'All' || rev.status === reviewFilter;
    const q = reviewSearch.trim().toLowerCase();
    const prod = products.find(p => p.id === rev.productId);
    const matchSearch = !q ||
      rev.customerName?.toLowerCase().includes(q) ||
      rev.city?.toLowerCase().includes(q) ||
      rev.comment?.toLowerCase().includes(q) ||
      prod?.nameUrdu?.toLowerCase().includes(q) ||
      prod?.nameEn?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#f0f0f1] text-gray-800 flex flex-col font-urdu antialiased dir-rtl selection:bg-emerald-600 selection:text-white">
      
      {/* WordPress-style Top Admin Bar */}
      <header className="bg-[#1d2327] text-[#c3c4c7] sticky top-0 z-40 border-b border-black/40 shadow-sm">
        <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-2 text-xs">
          
          {/* Right Section: Mobile Hamburger, Brand, Quick View Store */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Hamburger Drawer Button */}
            <button
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              className="lg:hidden p-1.5 rounded bg-[#2c3338] text-white hover:bg-emerald-600 transition cursor-pointer"
              title="مینو کھولیں / بند کریں"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Store Branding */}
            <div className="flex items-center gap-2">
              {settings?.logoUrl ? (
                <div className="w-7 h-7 rounded-lg overflow-hidden bg-white p-0.5 flex items-center justify-center">
                  <img src={settings.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className="w-7 h-7 rounded-lg bg-emerald-700 text-white flex items-center justify-center font-bold text-xs">
                  HP
                </div>
              )}
              <div className="hidden sm:block">
                <span className="font-bold text-white text-sm">
                  {settings.storeNameUrdu || 'ہمالین پنسار سٹور'}
                </span>
                <span className="text-[10px] text-gray-400 mr-2">ورڈپریس اسٹائل کنٹرول پینل</span>
              </div>
            </div>

            {/* Quick Visit Store Link */}
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const isSubfolder = window.location.pathname.includes('/himalayan');
                  const cleanUrl = isSubfolder ? '/himalayan/' : '/';
                  window.history.pushState(null, '', cleanUrl);
                }
                onBackToStore();
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#2c3338] hover:bg-emerald-700 text-gray-200 hover:text-white text-xs font-medium transition cursor-pointer"
              title="سٹور کا لائیو پیش منظر دیکھیں"
            >
              <Home className="w-3.5 h-3.5 text-emerald-400" />
              <span className="hidden md:inline">سٹور دیکھیں</span>
            </button>

            {/* Quick Add Buttons */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setProductModalOpen(true);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded hover:bg-[#2c3338] text-gray-300 hover:text-white text-xs transition cursor-pointer"
                title="نئی پراڈکٹ شامل کریں"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-400" />
                <span>نئی دوا / پراڈکٹ</span>
              </button>

              <button
                onClick={() => setActiveTab('blogs')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded hover:bg-[#2c3338] text-gray-300 hover:text-white text-xs transition cursor-pointer"
                title="نیا بلاگ مضمون لکھیں"
              >
                <Plus className="w-3.5 h-3.5 text-blue-400" />
                <span>نیا مضمون</span>
              </button>
            </div>
          </div>

          {/* Left Section: Live Clock, Server Status, User Profile, Logout */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Realtime Clock & Date */}
            <div className="hidden lg:flex items-center gap-1.5 text-[11px] text-gray-400 bg-[#2c3338] px-2.5 py-1 rounded font-sans">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span>{currentTime.toLocaleTimeString('ur-PK', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            </div>

            {/* Hostinger Server Sync Status Badge */}
            <div className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-bold border transition ${
              serverSyncStatus === 'connected'
                ? 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40 shadow-xs'
                : 'bg-amber-950/80 text-amber-300 border-amber-500/40'
            }`}>
              <span className={`w-2 h-2 rounded-full animate-pulse ${
                serverSyncStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400'
              }`} />
              <span className="hidden md:inline">{serverSyncStatus === 'connected' ? 'ہوسٹنگر سرور منسلک' : 'لوکل کیشے'}</span>
            </div>

            {/* Admin Avatar & Greeting */}
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#2c3338]/60 text-gray-200">
              <div className="w-6 h-6 rounded-full bg-emerald-800 text-white flex items-center justify-center font-bold text-[10px]">
                ح
              </div>
              <span className="hidden sm:inline text-xs">{settings.hakimNameUrdu || 'حکیم ایڈمن'}</span>
            </div>

            {/* Log Out */}
            <button
              onClick={async () => {
                await logoutAdmin();
                if (typeof window !== 'undefined') {
                  const isSubfolder = window.location.pathname.includes('/himalayan');
                  const cleanUrl = isSubfolder ? '/himalayan/' : '/';
                  window.history.replaceState(null, '', cleanUrl);
                }
                onBackToStore();
              }}
              className="p-1.5 rounded bg-[#2c3338] hover:bg-rose-900 text-gray-300 hover:text-white transition cursor-pointer"
              title="لاگ آؤٹ"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

        </div>
      </header>

      {/* Admin Layout Container (Sidebar + Content) */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* WordPress-Style Sidebar (Right Side in RTL) */}
        {/* Mobile Backdrop */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-xs transition-opacity"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        <aside
          className={`
            fixed lg:static top-[41px] lg:top-0 bottom-0 right-0 z-50
            bg-[#1d2327] text-[#c3c4c7] flex flex-col justify-between shrink-0
            transition-all duration-200 border-l border-[#2c3338] shadow-xl lg:shadow-none
            ${sidebarCollapsed ? 'w-18' : 'w-64'}
            ${mobileSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          `}
        >
          {/* Sidebar Nav Items */}
          <div className="flex-1 overflow-y-auto py-3 px-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-700">
            {/* Menu Header */}
            {!sidebarCollapsed && (
              <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                مرکزی مینیو
              </div>
            )}

            {/* 1. Dashboard Overview */}
            <button
              onClick={() => {
                setActiveTab('dashboard');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'dashboard'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="ڈیش بورڈ اوور ویو"
            >
              <LayoutDashboard className={`w-4 h-4 shrink-0 ${activeTab === 'dashboard' ? 'text-white' : 'text-blue-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">📊 ڈیش بورڈ (Dashboard)</span>}
            </button>

            {/* 2. Orders (WooCommerce style) */}
            <button
              onClick={() => {
                setActiveTab('orders');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'orders'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="کسٹمر آرڈرز"
            >
              <div className="flex items-center gap-3 min-w-0">
                <ShoppingBag className={`w-4 h-4 shrink-0 ${activeTab === 'orders' ? 'text-white' : 'text-emerald-400 group-hover:text-white'}`} />
                {!sidebarCollapsed && <span className="truncate">🛍️ ووکامرس آرڈرز</span>}
              </div>
              {!sidebarCollapsed && pendingOrdersCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-slate-950 font-sans">
                  {pendingOrdersCount}
                </span>
              )}
            </button>

            {/* 3. Products & Stock */}
            <button
              onClick={() => {
                setActiveTab('products');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'products'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="پروڈکٹس اور سٹاک"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Package className={`w-4 h-4 shrink-0 ${activeTab === 'products' ? 'text-white' : 'text-purple-400 group-hover:text-white'}`} />
                {!sidebarCollapsed && <span className="truncate">📦 ادویات و سٹاک</span>}
              </div>
              {!sidebarCollapsed && outOfStockCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/90 text-white font-sans">
                  {outOfStockCount}
                </span>
              )}
            </button>

            {/* 4. Categories & Ailments */}
            <button
              onClick={() => {
                setActiveTab('categories');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'categories'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="کیٹیگریز اور امراض فلٹرز"
            >
              <FolderTree className={`w-4 h-4 shrink-0 ${activeTab === 'categories' ? 'text-white' : 'text-amber-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">📁 اقسام و امراض</span>}
            </button>

            {/* 5. Media Library */}
            <button
              onClick={() => {
                setActiveTab('media');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'media'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="میڈیا لائبریری"
            >
              <ImageIcon className={`w-4 h-4 shrink-0 ${activeTab === 'media' ? 'text-white' : 'text-teal-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">🖼️ میڈیا لائبریری (Media)</span>}
            </button>

            {/* 6. Blog & Articles */}
            <button
              onClick={() => {
                setActiveTab('blogs');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'blogs'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="طبی مضامین و بلاگ"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className={`w-4 h-4 shrink-0 ${activeTab === 'blogs' ? 'text-white' : 'text-emerald-400 group-hover:text-white'}`} />
                {!sidebarCollapsed && <span className="truncate">📝 مضامین و بلاگ</span>}
              </div>
              {!sidebarCollapsed && (blogs || []).length > 0 && (
                <span className="text-[10px] text-gray-400 font-sans">
                  {(blogs || []).length}
                </span>
              )}
            </button>

            {/* 7. Pages */}
            <button
              onClick={() => {
                setActiveTab('pages');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'pages'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="ویب پیجز مینجمنٹ"
            >
              <Layout className={`w-4 h-4 shrink-0 ${activeTab === 'pages' ? 'text-white' : 'text-cyan-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">📄 ویب پیجز (Pages)</span>}
            </button>

            {/* 8. Customer Reviews */}
            <button
              onClick={() => {
                setActiveTab('reviews');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'reviews'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="کسٹمر ریویوز"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Star className={`w-4 h-4 shrink-0 ${activeTab === 'reviews' ? 'text-white' : 'text-amber-400 fill-amber-400/30 group-hover:text-white'}`} />
                {!sidebarCollapsed && <span className="truncate">⭐ کسٹمر ریویوز</span>}
              </div>
              {!sidebarCollapsed && (reviews || []).length > 0 && (
                <span className="text-[10px] text-gray-400 font-sans">
                  {(reviews || []).length}
                </span>
              )}
            </button>

            {/* Divider */}
            <div className="my-2 border-t border-[#2c3338]" />
            {!sidebarCollapsed && (
              <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                سیٹنگز اور سسٹم
              </div>
            )}

            {/* 9. CMS Content */}
            <button
              onClick={() => {
                setActiveTab('cms');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'cms'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="ویب سائٹ مواد و ہوم پیج CMS"
            >
              <Sparkles className={`w-4 h-4 shrink-0 ${activeTab === 'cms' ? 'text-white' : 'text-purple-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">🌐 مواد و ہوم پیج (CMS)</span>}
            </button>

            {/* 10. Store Settings */}
            <button
              onClick={() => {
                setActiveTab('settings');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'settings'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="سٹور اور واٹس ایپ ترتیبات"
            >
              <Settings className={`w-4 h-4 shrink-0 ${activeTab === 'settings' ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">⚙️ سٹور و واٹس ایپ سیٹنگز</span>}
            </button>

            {/* 11. Security & 2FA Dedicated Tab */}
            <button
              onClick={() => {
                setActiveTab('security');
                setMobileSidebarOpen(false);
                handleFetchTotpSetup();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'security'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="ایڈمن لاگ ان، 2FA، خفیہ URL اور سیکیورٹی سیٹنگز"
            >
              <ShieldCheck className={`w-4 h-4 shrink-0 ${activeTab === 'security' ? 'text-white' : 'text-emerald-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">🛡️ ایڈمن سیکیورٹی و 2FA</span>}
            </button>

            {/* 12. Backup & Sync */}
            <button
              onClick={() => {
                setActiveTab('backup');
                setMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition text-right group cursor-pointer ${
                activeTab === 'backup'
                  ? 'bg-[#2271b1] text-white font-bold shadow-xs'
                  : 'hover:bg-[#2c3338] hover:text-white'
              }`}
              title="بیک اپ و سنک"
            >
              <Download className={`w-4 h-4 shrink-0 ${activeTab === 'backup' ? 'text-white' : 'text-amber-400 group-hover:text-white'}`} />
              {!sidebarCollapsed && <span className="flex-1">💾 بیک اپ و گٹ سنک</span>}
            </button>

          </div>

          {/* Sidebar Collapse Toggle Button at bottom */}
          <div className="p-2 border-t border-[#2c3338] hidden lg:block">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded hover:bg-[#2c3338] text-gray-400 hover:text-white text-xs transition cursor-pointer"
              title={sidebarCollapsed ? 'سائیڈ بار کھولیں' : 'سائیڈ بار سمیٹیں'}
            >
              {sidebarCollapsed ? (
                <ChevronLeft className="w-4 h-4" />
              ) : (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span>کولیپس مینو (Collapse)</span>
                </>
              )}
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-[#f0f0f1] p-4 sm:p-6 lg:p-8 min-h-screen">
          <div className="max-w-7xl mx-auto space-y-6 pb-12">
            
            {/* Render Tab 0: WordPress Dashboard Overview */}
            {activeTab === 'dashboard' && (
              <DashboardOverview
                onNavigateTab={(tab) => setActiveTab(tab)}
                onOpenNewProduct={() => {
                  setEditingProduct(null);
                  setProductModalOpen(true);
                }}
                onOpenNewBlog={() => {
                  setActiveTab('blogs');
                }}
                onSelectOrder={() => {
                  setActiveTab('orders');
                }}
              />
            )}

            {/* TAB 1: ORDERS MANAGEMENT */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <h3 className="font-bold text-base text-gray-900">
                موصول ہونے والے تمام کسٹمر آرڈرز
              </h3>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">فلٹر بلحاظ اسٹیٹس:</span>
                <select
                  value={orderStatusFilter}
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                  className="p-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none"
                >
                  <option value="All">تمام آرڈرز</option>
                  <option value="Pending">پینڈنگ (Pending)</option>
                  <option value="Processing">زیرِ عمل (Processing)</option>
                  <option value="Shipped">روانہ کر دیا (Shipped)</option>
                  <option value="Delivered">پہنچ گیا (Delivered)</option>
                  <option value="Cancelled">منسوخ (Cancelled)</option>
                </select>
              </div>
            </div>

            {filteredOrders.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                کوئی آرڈر نہیں ملا۔
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-bold">
                    <tr>
                      <th className="p-3">آرڈر نمبر و تاریخ</th>
                      <th className="p-3">خریدار کی تفصیل</th>
                      <th className="p-3">اشیاء</th>
                      <th className="p-3">کل رقم و ادائیگی</th>
                      <th className="p-3">اسٹیٹس</th>
                      <th className="p-3">ایکشن</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredOrders.map(order => (
                      <tr key={order.id} className="hover:bg-gray-50/80 transition">
                        <td className="p-3 align-top font-mono font-bold text-pansar-900">
                          #{order.id}
                          <div className="text-[11px] font-sans font-normal text-gray-400 mt-0.5">
                            {order.date}
                          </div>
                        </td>

                        <td className="p-3 align-top">
                          <div className="font-bold text-gray-900">{order.customerName}</div>
                          <div className="text-emerald-700 font-sans dir-ltr text-right">{order.phone}</div>
                          <div className="text-[11px] text-gray-500 max-w-xs truncate">{order.address}, {order.city}</div>
                          {order.notes && (
                            <div className="text-[10px] text-amber-700 bg-amber-50 p-1 rounded mt-1">
                              نوٹ: {order.notes}
                            </div>
                          )}
                        </td>

                        <td className="p-3 align-top max-w-xs">
                          <ul className="space-y-1">
                            {order.items.map((it, i) => (
                              <li key={i} className="text-gray-700">
                                • {it.name} ({it.variant}) × <strong>{it.quantity}</strong>
                              </li>
                            ))}
                          </ul>
                        </td>

                        <td className="p-3 align-top">
                          <div className="font-extrabold text-sm text-pansar-900 font-sans">
                            {formatPrice(order.totalAmount, lang)}
                          </div>
                          <div className="text-[11px] font-bold text-gray-600 mt-0.5 font-urdu">
                            {order.paymentMethod === 'cod' ? 'کیش آن ڈلیوری' : 'آن لائن ادائیگی'}
                          </div>

                          {order.paymentAccount && (
                            <div className="text-[10px] text-emerald-900 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 mt-0.5 inline-block font-urdu font-bold">
                              {order.paymentAccount === 'bank' ? 'یو بی ایل بینک / QR' : 'ایزی پیسہ'}
                            </div>
                          )}

                          {/* COD Advance details badge */}
                          {order.paymentMethod === 'cod' && order.advanceAmount ? (
                            <div className="mt-1 text-[10px] space-y-0.5 bg-amber-50 p-1.5 rounded-lg border border-amber-200/80">
                              <div className="text-amber-900 font-bold">
                                ایڈوانس: <span className="font-sans font-black text-emerald-800">Rs. {order.advanceAmount}</span>
                              </div>
                              <div className="text-gray-700">
                                بقیہ بوقتِ ترسیل: <span className="font-sans font-bold">Rs. {order.remainingAmount}</span>
                              </div>
                            </div>
                          ) : null}

                          {order.transactionRef && (
                            <div className="text-[10px] text-gray-500 font-mono mt-1 truncate max-w-[140px]" title={order.transactionRef}>
                              TID: {order.transactionRef}
                            </div>
                          )}

                          {order.paymentProof ? (
                            <button
                              type="button"
                              onClick={() => setViewingReceiptModal({
                                img: order.paymentProof,
                                orderId: order.id,
                                customerName: order.customerName,
                                tid: order.transactionRef,
                                amount: order.totalAmount
                              })}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 px-2 py-1 rounded-md transition shadow-xs cursor-pointer"
                              title="ادائیگی کی رسید / اسکرین شاٹ دیکھیں"
                            >
                              <Eye className="w-3.5 h-3.5 text-emerald-700" />
                              <span>رسید دیکھیں</span>
                            </button>
                          ) : null}

                          {order.deliveryFee === 0 && (
                            <span className="inline-block text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-bold mt-1">
                              مفت ڈلیوری
                            </span>
                          )}
                        </td>

                        <td className="p-3 align-top">
                          <select
                            value={order.status}
                            onChange={(e) => {
                              const newStatus = e.target.value;
                              updateOrderStatus(order.id, newStatus);
                            }}
                            className={`p-1.5 rounded-lg border text-xs font-bold font-sans ${
                              order.status === 'Delivered'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : order.status === 'Shipped'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : order.status === 'Pending'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-gray-50 text-gray-700 border-gray-200'
                            }`}
                          >
                            <option value="Pending">Pending (پینڈنگ)</option>
                            <option value="Processing">Processing (زیرِ عمل)</option>
                            <option value="Shipped">Shipped (روانہ شدہ)</option>
                            <option value="Delivered">Delivered (پہنچ گیا)</option>
                            <option value="Cancelled">Cancelled (منسوخ)</option>
                          </select>
                        </td>

                        <td className="p-3 align-top space-y-1.5 min-w-[140px]">
                          <button
                            type="button"
                            onClick={() => sendOrderStatusWhatsApp(order, order.status)}
                            className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs flex items-center gap-1 transition w-full justify-center font-bold cursor-pointer"
                            title="گاہک کو واٹس ایپ پر اپ ڈیٹ بھیجیں"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>واٹس ایپ اطلاع</span>
                          </button>

                          {/* Quick action: Send WhatsApp review request */}
                          <button
                            type="button"
                            onClick={() => sendReviewRequestWhatsApp(order)}
                            className="p-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-800 text-xs flex items-center gap-1 transition w-full justify-center font-bold cursor-pointer"
                            title="کسٹمر کو واٹس ایپ پر ریویو کی درخواست بھیجیں"
                          >
                            <MessageSquare className="w-3.5 h-3.5 text-teal-600" />
                            <span>ریویو درخواست (WhatsApp)</span>
                          </button>

                          {/* Quick action: Add review on customer's behalf */}
                          <button
                            type="button"
                            onClick={() => {
                              handleSelectOrderForReview(order);
                              setAdminAddReviewModal(true);
                            }}
                            className="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs flex items-center gap-1 transition w-full justify-center font-bold cursor-pointer border border-amber-200"
                            title="اس کسٹمر کے آرڈر سے ریویو و ریٹنگ درج کریں"
                          >
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                            <span>کسٹمر کا ریویو درج کریں</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`کیا آپ آرڈر #${order.id} کو حذف کرنا چاہتے ہیں؟`)) {
                                deleteOrder(order.id);
                              }
                            }}
                            className="p-1.5 rounded-lg hover:bg-rose-50 text-gray-400 hover:text-rose-600 text-xs flex items-center gap-1 transition w-full justify-center cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>حذف</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: PRODUCTS MANAGEMENT */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-bold text-base text-gray-900">
                  ادویات اور جڑی بوٹیوں کی مینجمنٹ ({products.length})
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="پروڈکٹ تلاش کریں..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="py-1.5 px-8 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-pansar-700"
                  />
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute top-1/2 -translate-y-1/2 left-2.5" />
                </div>
              </div>

              {/* Stock Status Filter Chips */}
              <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setProductStockFilter('All')}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    productStockFilter === 'All'
                      ? 'bg-white text-gray-900 shadow-2xs'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  تمام ({products.length})
                </button>
                <button
                  type="button"
                  onClick={() => setProductStockFilter('InStock')}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    productStockFilter === 'InStock'
                      ? 'bg-emerald-600 text-white shadow-2xs'
                      : 'text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  دستیاب ({products.filter(p => p.inStock).length})
                </button>
                <button
                  type="button"
                  onClick={() => setProductStockFilter('OutOfStock')}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    productStockFilter === 'OutOfStock'
                      ? 'bg-rose-600 text-white shadow-2xs'
                      : 'text-rose-700 hover:bg-rose-50'
                  }`}
                >
                  آؤٹ آف اسٹاک ({outOfStockCount})
                </button>
              </div>

              <button
                onClick={() => {
                  setEditingProduct(null);
                  setProductModalOpen(true);
                }}
                className="py-2.5 px-4 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs flex items-center gap-1.5 shadow cursor-pointer transition"
              >
                <Plus className="w-4 h-4" />
                <span>نئی دوا / پروڈکٹ شامل کریں</span>
              </button>
            </div>

            {/* Product Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-bold">
                  <tr>
                    <th className="p-3">تصویر</th>
                    <th className="p-3">نام دوا و مزاج</th>
                    <th className="p-3">کیٹیگری</th>
                    <th className="p-3">اوزان و قیمتیں</th>
                    <th className="p-3">سٹاک کی حالت (تبدیل کریں)</th>
                    <th className="p-3">ایکشن</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProducts.map(product => (
                    <tr key={product.id} className="hover:bg-gray-50 transition">
                      <td className="p-3 align-middle">
                        <img 
                          src={product.image} 
                          alt={product.nameUrdu} 
                          className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                        />
                      </td>

                      <td className="p-3 align-middle">
                        <div className="font-bold text-sm text-gray-900">{product.nameUrdu}</div>
                        <div className="text-[11px] text-gray-400 font-sans">{product.nameEn}</div>
                        <span className="text-[10px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded font-bold">
                          {formatMizajDisplay(product, mizajList, lang)}
                        </span>
                      </td>

                      <td className="p-3 align-middle">
                        <span className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md text-[11px]">
                          {product.category}
                        </span>
                      </td>

                      <td className="p-3 align-middle">
                        <div className="space-y-0.5">
                          {product.variants?.map((v, idx) => (
                            <div key={idx} className="text-gray-700">
                              {v.weight}: <strong className="font-sans">{formatPrice(v.price, lang)}</strong>
                            </div>
                          ))}
                        </div>
                      </td>

                      <td className="p-3 align-middle">
                        <button
                          type="button"
                          onClick={() => toggleStock(product.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-2xs border ${
                            product.inStock
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                              : 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100'
                          }`}
                          title="سٹاک کی حالت تبدیل کرنے کے لیے کلک کریں (Click to Toggle)"
                        >
                          <span className={`w-2 h-2 rounded-full ${product.inStock ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                          <span>{product.inStock ? 'دستیاب ہے (In Stock)' : 'آؤٹ آف اسٹاک (Out of Stock)'}</span>
                        </button>
                      </td>

                      <td className="p-3 align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenEditProduct(product)}
                            className="p-1.5 rounded-lg bg-gray-100 hover:bg-pansar-100 text-gray-700 hover:text-pansar-900 transition"
                            title="ترمیم کریں"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`کیا آپ "${product.nameUrdu}" کو ڈیلیٹ کرنا چاہتے ہیں؟`)) {
                                deleteProduct(product.id);
                              }
                            }}
                            className="p-1.5 rounded-lg bg-gray-100 hover:bg-rose-100 text-gray-400 hover:text-rose-600 transition"
                            title="حذف کریں"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: CATEGORIES & AILMENTS MANAGEMENT */}
        {activeTab === 'categories' && (
          <CategoryManager />
        )}

        {/* TAB: CUSTOMER REVIEWS MANAGEMENT */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-200 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h3 className="font-bold text-base text-gray-900 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span>کسٹمر ریٹنگز اور ریویوز کی مینجمنٹ (Reviews Management)</span>
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-urdu">
                  یہاں سے آپ خریداروں کے موصول شدہ ریویوز دیکھ سکتے ہیں، منظور یا ڈیلیٹ کر سکتے ہیں، یا کلینک کے مریضوں کے مصدقہ تاثرات خود شامل کر سکتے ہیں۔
                </p>
              </div>

              <button
                type="button"
                onClick={() => setAdminAddReviewModal(true)}
                className="px-4 py-2.5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs flex items-center gap-1.5 transition shadow cursor-pointer font-urdu"
              >
                <Plus className="w-4 h-4 text-amber-400" />
                <span>نیا ریویو شامل کریں (Add Testimonial)</span>
              </button>
            </div>

            {/* Practical Guide for Admin on Collecting Reviews */}
            <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-amber-50 p-4 rounded-2xl border border-emerald-200 text-xs space-y-2.5">
              <div className="flex items-center gap-2 text-emerald-950 font-bold text-sm">
                <Sparkles className="w-4 h-4 text-emerald-700" />
                <span>گاہکوں سے ریٹنگز اور کمنٹس حاصل کرنے کا طریقہ کار و نظام:</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-gray-700">
                <div className="bg-white/90 p-3 rounded-xl border border-emerald-100 space-y-1 shadow-2xs">
                  <div className="font-bold text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>1. آرڈر تصدیقی رسید پر فوری نوٹس:</span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-gray-600">
                    خریدار جب بھی آرڈر کرتا ہے، اسے رسید پر واضح طور پر آرڈر نمبر کے ساتھ دوا کے استعمال کے بعد ریویو دینے کی ترغیب دی جاتی ہے۔
                  </p>
                </div>
                <div className="bg-white/90 p-3 rounded-xl border border-emerald-100 space-y-1 shadow-2xs">
                  <div className="font-bold text-teal-900 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-teal-600" />
                    <span>2. واٹس ایپ پر 1-کلک ریویو درخواست:</span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-gray-600">
                    "آرڈرز کی فہرست" میں ہر آرڈر کے آگے <strong>"ریویو درخواست (WhatsApp)"</strong> کا بٹن ہے جس سے گاہک کو شائستہ یاد دہانی اور سٹور کا لنک چلا جاتا ہے۔
                  </p>
                </div>
                <div className="bg-white/90 p-3 rounded-xl border border-emerald-100 space-y-1 shadow-2xs">
                  <div className="font-bold text-amber-900 flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                    <span>3. کسٹمر کے آرڈر سے خود ریویو درج کریں:</span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-gray-600">
                    اگر گاہک واٹس ایپ یا کال پر افاقہ بتائے تو <strong>"کسٹمر کا ریویو درج کریں"</strong> دبائیں، آرڈر نمبر، نام، شہر و دوا خودکار بھر جائے گی!
                  </p>
                </div>
              </div>
            </div>

            {/* Filters & Search */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={reviewSearch}
                  onChange={(e) => setReviewSearch(e.target.value)}
                  placeholder="کسٹمر کا نام، تبصرہ یا پروڈکٹ تلاش کریں..."
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:border-pansar-700"
                />
              </div>

              <div className="flex items-center gap-2 text-xs font-urdu">
                <span className="text-gray-500">حالت:</span>
                <select
                  value={reviewFilter}
                  onChange={(e) => setReviewFilter(e.target.value)}
                  className="bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs focus:outline-none"
                >
                  <option value="All">تمام ریویوز ({(reviews || []).length})</option>
                  <option value="approved">صرف منظور شدہ ({(reviews || []).filter(r => r.status === 'approved').length})</option>
                  <option value="pending">زیرِ التواء ({(reviews || []).filter(r => r.status === 'pending').length})</option>
                </select>
              </div>
            </div>

            {/* Reviews Cards / List */}
            {filteredReviews.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-3xl p-6 font-urdu">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <h4 className="font-bold text-gray-700 text-sm">کوئی ریویو دستیاب نہیں ہے</h4>
                <p className="text-xs text-gray-400 mt-1">آپ اوپر دیے گئے بٹن پر کلک کر کے مریضوں کے نئے تاثرات شامل کر سکتے ہیں۔</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredReviews.map((rev) => {
                  const prod = products.find(p => p.id === rev.productId);
                  return (
                    <div key={rev.id} className="bg-white rounded-2xl p-5 border border-gray-200 shadow-xs space-y-3 relative hover:border-pansar-200 transition">
                      {/* Product Header */}
                      <div className="flex items-center gap-3 border-b border-gray-100 pb-2.5">
                        {prod?.image && (
                          <img src={prod.image} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] text-gray-400 font-urdu block">پروڈکٹ:</span>
                          <span className="font-bold text-xs text-gray-900 font-title truncate block">
                            {prod?.nameUrdu || rev.productId}
                          </span>
                        </div>
                        <div className="text-left shrink-0">
                          <div className="flex items-center text-amber-400 gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < rev.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                            ))}
                          </div>
                          <span className="text-[10px] text-gray-400 font-sans">{rev.date}</span>
                        </div>
                      </div>

                      {/* Customer Details */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-gray-800 font-urdu">{rev.customerName}</span>
                          {rev.city && <span className="text-gray-400 text-[11px]">({rev.city})</span>}
                        </div>
                        {rev.orderId && (
                          <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-bold font-sans">
                            آرڈر: #{rev.orderId}
                          </span>
                        )}
                      </div>

                      {/* Comment */}
                      <p className="text-xs text-gray-700 font-urdu leading-relaxed bg-gray-50/70 p-3 rounded-xl border border-gray-100">
                        "{rev.comment}"
                      </p>

                      {/* Status & Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          rev.status === 'approved'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {rev.status === 'approved' ? 'منظور شدہ (Approved)' : 'زیرِ التواء (Pending)'}
                        </span>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateReviewStatus(rev.id, rev.status === 'approved' ? 'pending' : 'approved')}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition font-urdu cursor-pointer ${
                              rev.status === 'approved'
                                ? 'bg-gray-100 hover:bg-amber-100 text-gray-700'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                          >
                            {rev.status === 'approved' ? 'غیر منظور کریں' : 'منظور کریں'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('کیا آپ اس ریویو کو مستقل حذف کرنا چاہتے ہیں؟')) {
                                deleteReview(rev.id);
                              }
                            }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                            title="ڈیلیٹ کریں"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Admin Add Review Modal */}
            {adminAddReviewModal && (
              <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-4 font-urdu border border-gray-100 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span>کسٹمر کی طرف سے تصدیق شدہ ریویو و ریٹنگ درج کریں</span>
                      </h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        آرڈر نمبر لکھیں یا منتخب کریں، گاہک کی تفصیلات خودکار بھر جائیں گی۔
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAdminAddReviewModal(false);
                        setSelectedOrderForReview(null);
                      }}
                      className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Order Lookup & Auto-fill Section */}
                  <div className="bg-amber-50/70 p-3.5 rounded-2xl border border-amber-200 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <label className="block font-bold text-amber-950 text-xs flex items-center gap-1.5">
                        <ShoppingBag className="w-3.5 h-3.5 text-amber-700" />
                        <span>کسٹمر کا آرڈر منتخب کریں یا آرڈر نمبر لکھیں:</span>
                      </label>
                      {newReviewForm.orderId && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewReviewForm({
                              productId: products[0]?.id || '',
                              customerName: '',
                              city: '',
                              orderId: '',
                              rating: 5,
                              comment: ''
                            });
                            setSelectedOrderForReview(null);
                          }}
                          className="text-[11px] text-rose-600 hover:underline font-bold cursor-pointer"
                        >
                          صاف کریں (Reset)
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-600 mb-0.5">آرڈرز کی لسٹ میں سے چنیں:</label>
                        <select
                          value={newReviewForm.orderId || ''}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            if (!selectedId) {
                              setSelectedOrderForReview(null);
                              setNewReviewForm(prev => ({ ...prev, orderId: '', customerName: '', city: '' }));
                              return;
                            }
                            const matched = orders.find(o => o.id === selectedId);
                            if (matched) {
                              handleSelectOrderForReview(matched);
                            }
                          }}
                          className="w-full p-2 rounded-xl border border-amber-300 text-xs bg-white font-sans truncate"
                        >
                          <option value="">-- کسٹمر کا آرڈر منتخب کریں --</option>
                          {orders.map(o => (
                            <option key={o.id} value={o.id}>
                              #{o.id} - {o.customerName} ({o.city})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-600 mb-0.5">یا آرڈر نمبر ٹائپ کریں (مثلاً HP-9041):</label>
                        <input
                          type="text"
                          value={newReviewForm.orderId}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewReviewForm(prev => ({ ...prev, orderId: val }));
                            const clean = val.trim().toUpperCase();
                            const matched = orders.find(o => o.id?.toUpperCase() === clean || o.id?.toUpperCase() === `HP-${clean}`);
                            if (matched) {
                              handleSelectOrderForReview(matched);
                            }
                          }}
                          placeholder="HP-9041"
                          className="w-full p-2 rounded-xl border border-amber-300 text-xs font-mono dir-ltr text-left uppercase bg-white font-bold"
                        />
                      </div>
                    </div>

                    {/* If order matched, show verified pill & item selector */}
                    {selectedOrderForReview && (
                      <div className="bg-white p-3 rounded-xl border border-emerald-300 text-xs space-y-2 animate-fade-in">
                        <div className="flex items-center justify-between text-emerald-900 font-bold border-b border-gray-100 pb-1.5">
                          <span className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span>تصدیق شدہ آرڈر مل گیا: #{selectedOrderForReview.id}</span>
                          </span>
                          <span className="text-gray-500 font-sans text-[11px] dir-ltr">{selectedOrderForReview.phone}</span>
                        </div>
                        
                        <div className="text-gray-700">
                          گاہک: <strong>{selectedOrderForReview.customerName}</strong> ({selectedOrderForReview.city})
                        </div>

                        {/* Quick select buttons for purchased items */}
                        {selectedOrderForReview.items && selectedOrderForReview.items.length > 0 && (
                          <div className="pt-1">
                            <span className="text-[11px] font-bold text-gray-600 block mb-1">
                              کسٹمر نے اس آرڈر میں یہ ادویات خریدی تھیں (ریویو کے لیے منتخب کریں):
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedOrderForReview.items.map((item, idx) => {
                                const matchedProd = products.find(p => p.nameUrdu === item.name || p.nameEn === item.name);
                                const isSelected = newReviewForm.productId === (matchedProd?.id || products[0]?.id);
                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                      if (matchedProd) {
                                        setNewReviewForm(prev => ({ ...prev, productId: matchedProd.id }));
                                      }
                                    }}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer ${
                                      isSelected
                                        ? 'bg-emerald-700 text-white shadow-xs'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                                    }`}
                                  >
                                    <span>{item.name}</span>
                                    {isSelected && <Check className="w-3 h-3" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!newReviewForm.comment.trim() || !newReviewForm.customerName.trim()) return;
                      addReview({
                        productId: newReviewForm.productId || products[0]?.id,
                        customerName: newReviewForm.customerName,
                        city: newReviewForm.city,
                        orderId: newReviewForm.orderId,
                        rating: Number(newReviewForm.rating) || 5,
                        comment: newReviewForm.comment,
                        status: 'approved'
                      });
                      setAdminAddReviewModal(false);
                      setSelectedOrderForReview(null);
                      setNewReviewForm({
                        productId: products[0]?.id || '',
                        customerName: '',
                        city: '',
                        orderId: '',
                        rating: 5,
                        comment: ''
                      });
                    }}
                    className="space-y-3 text-xs"
                  >
                    <div>
                      <label className="block font-bold text-gray-700 mb-1">دوا / پروڈکٹ منتخب کریں:</label>
                      <select
                        value={newReviewForm.productId || products[0]?.id}
                        onChange={(e) => setNewReviewForm({ ...newReviewForm, productId: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs bg-gray-50 focus:outline-none"
                      >
                        {products.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.nameUrdu} ({p.nameEn || p.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block font-bold text-gray-700 mb-1">مریض یا خریدار کا نام *:</label>
                        <input
                          type="text"
                          required
                          value={newReviewForm.customerName}
                          onChange={(e) => setNewReviewForm({ ...newReviewForm, customerName: e.target.value })}
                          placeholder="مثلاً: محمد اکرم"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs bg-gray-50/50"
                        />
                      </div>
                      <div>
                        <label className="block font-bold text-gray-700 mb-1">شہر:</label>
                        <input
                          type="text"
                          value={newReviewForm.city}
                          onChange={(e) => setNewReviewForm({ ...newReviewForm, city: e.target.value })}
                          placeholder="مثلاً: راولپنڈی"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs bg-gray-50/50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold text-gray-700 mb-1">ریٹنگ (اسٹارز):</label>
                      <select
                        value={newReviewForm.rating}
                        onChange={(e) => setNewReviewForm({ ...newReviewForm, rating: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs bg-gray-50"
                      >
                        <option value="5">⭐⭐⭐⭐⭐ (5 - بہترین رزلٹ)</option>
                        <option value="4">⭐⭐⭐⭐ (4 - بہت اچھا)</option>
                        <option value="3">⭐⭐⭐ (3 - مناسب)</option>
                        <option value="2">⭐⭐ (2 - عام نتائج)</option>
                        <option value="1">⭐ (1 - مطمئن نہیں)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-gray-700 mb-1">
                        گاہک کے تاثرات / تبصرہ / افاقہ کی تفصیل *:
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={newReviewForm.comment}
                        onChange={(e) => setNewReviewForm({ ...newReviewForm, comment: e.target.value })}
                        placeholder="کسٹمر نے جو فائدہ بتایا وہ یہاں درج کریں، مثلاً: معدے کے درد اور گیس میں فوری آرام ملا..."
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs leading-relaxed"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => {
                          setAdminAddReviewModal(false);
                          setSelectedOrderForReview(null);
                        }}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                      >
                        منسوخ کریں
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white text-xs font-bold shadow cursor-pointer flex items-center gap-1.5"
                      >
                        <Check className="w-4 h-4" />
                        <span>ریویو محفوظ و شائع کریں</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: STORE SETTINGS */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-200 max-w-3xl space-y-6">
            <div className="border-b border-gray-100 pb-4">
              <h3 className="font-bold text-base text-gray-900">
                سٹور سیٹنگز اور واٹس ایپ کنفیگریشن
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                یہاں سے آپ کاروباری نام، واٹس ایپ نمبر، اور ادائیگی کے اکاؤنٹس تبدیل کر سکتے ہیں۔
              </p>
            </div>

            <form onSubmit={handleSaveSettings} className="space-y-4">
              
              {/* Store Logo & Browser Tab Favicon Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Store Official Brand Logo */}
                <ImageUploadField
                  label="سٹور و دواخانہ کا آفیشل لوگو (Store Logo)"
                  value={settingsForm.logoUrl || ''}
                  onChange={(val) => setSettingsForm({ ...settingsForm, logoUrl: val })}
                  maxWidth={400}
                  previewShape="square"
                  helperText="ہیڈر اور فوٹر میں نظر آنے والا مرکزی برانڈ لوگو۔"
                />

                {/* 2. Browser Tab Icon (Site Favicon) */}
                <div className="space-y-3">
                  <ImageUploadField
                    label="براؤزر ٹیب تصویر / سائٹ فیو آئیکون (Browser Tab Favicon)"
                    value={settingsForm.faviconUrl || ''}
                    onChange={(val) => setSettingsForm({ ...settingsForm, faviconUrl: val })}
                    maxWidth={128}
                    previewShape="square"
                    helperText="یہ تصویر یوزر کے براؤزر کے اوپر ٹیب اور بک مارکس میں نظر آتی ہے (PNG, SVG, ICO)۔"
                  />

                  {/* Live Browser Tab Simulation Mockup */}
                  <div className="bg-gray-100/90 rounded-2xl p-3 border border-gray-200/90 space-y-2.5">
                    <div className="text-[11px] font-bold text-gray-700 flex items-center justify-between">
                      <span>براؤزر ٹیب لائیو پیش منظر (Live Tab Preview):</span>
                      <span className="text-[10px] text-gray-400 font-sans">Chrome / Edge / Safari</span>
                    </div>

                    {/* Browser Tab Simulation */}
                    <div className="bg-[#dee1e6] p-1.5 rounded-xl flex items-center gap-1 text-xs">
                      <div className="bg-white rounded-t-lg px-3 py-1.5 flex items-center gap-2 max-w-[240px] shadow-2xs border-b-2 border-white">
                        <div className="w-4 h-4 rounded shrink-0 flex items-center justify-center overflow-hidden">
                          {settingsForm.faviconUrl ? (
                            <img
                              src={settingsForm.faviconUrl}
                              alt="Favicon"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <span className="text-xs">🌿</span>
                          )}
                        </div>
                        <span className="truncate text-[11px] font-bold text-gray-800 font-urdu">
                          {settingsForm.storeNameUrdu || 'ہمالین پنسار سٹور'}
                        </span>
                        <X className="w-3 h-3 text-gray-400 shrink-0 ml-auto" />
                      </div>
                      <span className="text-gray-400 text-xs px-1.5">+</span>
                    </div>

                    {/* Quick Preset Favicons */}
                    <div className="pt-1">
                      <span className="text-[10px] text-gray-600 font-bold block mb-1">
                        یا فوری تیار شدہ آئیکون منتخب کریں:
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSettingsForm({ ...settingsForm, faviconUrl: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>🌿</text></svg>" })}
                          className="px-2.5 py-1 bg-white hover:bg-emerald-50 rounded-lg border border-gray-200 text-[11px] flex items-center gap-1 transition cursor-pointer font-medium text-gray-700 shadow-2xs"
                          title="سبز پتہ"
                        >
                          <span>🌿</span>
                          <span>سبز پتہ (Default)</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSettingsForm({ ...settingsForm, faviconUrl: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>🏺</text></svg>" })}
                          className="px-2.5 py-1 bg-white hover:bg-amber-50 rounded-lg border border-gray-200 text-[11px] flex items-center gap-1 transition cursor-pointer font-medium text-gray-700 shadow-2xs"
                          title="ہربل مرتبان"
                        >
                          <span>🏺</span>
                          <span>مرتبان</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSettingsForm({ ...settingsForm, faviconUrl: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>🍃</text></svg>" })}
                          className="px-2.5 py-1 bg-white hover:bg-teal-50 rounded-lg border border-gray-200 text-[11px] flex items-center gap-1 transition cursor-pointer font-medium text-gray-700 shadow-2xs"
                          title="قدرتی بوٹی"
                        >
                          <span>🍃</span>
                          <span>قدرتی بوٹی</span>
                        </button>

                        {settingsForm.logoUrl && (
                          <button
                            type="button"
                            onClick={() => setSettingsForm({ ...settingsForm, faviconUrl: settingsForm.logoUrl })}
                            className="px-2.5 py-1 bg-white hover:bg-blue-50 rounded-lg border border-blue-200 text-[11px] text-blue-900 font-bold flex items-center gap-1 transition cursor-pointer shadow-2xs"
                            title="سٹور کا لوگو براؤزر ٹیب آئیکون کے طور پر لگائیں"
                          >
                            <span>🏷️</span>
                            <span>سٹور لوگو لگائیں</span>
                          </button>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    سٹور کا نام (اردو)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.storeNameUrdu}
                    onChange={(e) => setSettingsForm({ ...settingsForm, storeNameUrdu: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    دواخانہ کا نام
                  </label>
                  <input
                    type="text"
                    value={settingsForm.dawakhanaNameUrdu}
                    onChange={(e) => setSettingsForm({ ...settingsForm, dawakhanaNameUrdu: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    حکیم صاحب کا نام
                  </label>
                  <input
                    type="text"
                    value={settingsForm.hakimNameUrdu}
                    onChange={(e) => setSettingsForm({ ...settingsForm, hakimNameUrdu: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    واٹس ایپ نمبر (بغیر صفر یا پلس کے، مثلاً: 923470005578)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.whatsappNumber}
                    onChange={(e) => setSettingsForm({ ...settingsForm, whatsappNumber: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-right"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    رابطہ فون نمبر (کال کے لیے)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.phone}
                    onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-right"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    ڈلیوری چارجز (روپے میں)
                  </label>
                  <input
                    type="number"
                    value={settingsForm.deliveryFee}
                    onChange={(e) => setSettingsForm({ ...settingsForm, deliveryFee: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-right"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  مفت ڈلیوری کی کم از کم حد (روپے میں)
                </label>
                <input
                  type="number"
                  value={settingsForm.freeDeliveryThreshold}
                  onChange={(e) => setSettingsForm({ ...settingsForm, freeDeliveryThreshold: Number(e.target.value) })}
                  className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-right"
                />
              </div>

              {/* کیش آن ڈلیوری (COD) آن / آف اور پیشگی سیکیورٹی پالیسی */}
              <div className="bg-amber-50/80 rounded-2xl p-5 border border-amber-200/90 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-950 font-bold text-sm font-urdu">
                    <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0" />
                    <span>کیش آن ڈلیوری (Cash on Delivery - COD) سیٹنگ</span>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-bold font-urdu ${
                    (settingsForm.enableCod ?? true) ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-rose-100 text-rose-800 border border-rose-300'
                  }`}>
                    {(settingsForm.enableCod ?? true) ? 'آن (فعال)' : 'آف (بند)'}
                  </span>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <input
                    type="checkbox"
                    id="enableCod"
                    checked={settingsForm.enableCod ?? true}
                    onChange={(e) => setSettingsForm({ ...settingsForm, enableCod: e.target.checked })}
                    className="w-5 h-5 text-amber-600 rounded cursor-pointer accent-amber-600"
                  />
                  <label htmlFor="enableCod" className="text-xs font-bold text-gray-800 cursor-pointer select-none font-urdu">
                    کیش آن ڈلیوری (COD) کی سہولت گاہکوں کے لیے آن (فعال) رکھیں
                  </label>
                </div>

                {(settingsForm.enableCod ?? true) ? (
                  <div className="space-y-3 pt-2 border-t border-amber-200">
                    <p className="text-xs text-amber-900 leading-relaxed font-urdu">
                      کیش آن ڈلیوری آن ہے۔ نیچے دیے گئے آپشن سے آپ جعلی آرڈرز کی روک تھام کے لیے پیشگی ایڈوانس تصدیقی رقم وصول کر سکتے ہیں۔
                    </p>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="codAdvanceRequired"
                        checked={settingsForm.codAdvanceRequired ?? true}
                        onChange={(e) => setSettingsForm({ ...settingsForm, codAdvanceRequired: e.target.checked })}
                        className="w-4 h-4 text-amber-600 rounded cursor-pointer accent-amber-600"
                      />
                      <label htmlFor="codAdvanceRequired" className="text-xs font-bold text-gray-800 cursor-pointer select-none font-urdu">
                        کیش آن ڈلیوری پر پیشگی سیکیورٹی ایڈوانس رقم وصول کریں
                      </label>
                    </div>

                    {settingsForm.codAdvanceRequired && (
                      <div className="pt-1 max-w-sm">
                        <label className="block text-xs font-bold text-gray-700 mb-1 font-urdu">
                          کم از کم پیشگی ایڈوانس کی رقم (روپے میں):
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="100"
                            step="50"
                            value={settingsForm.codAdvanceAmount || 500}
                            onChange={(e) => setSettingsForm({ ...settingsForm, codAdvanceAmount: Number(e.target.value) })}
                            className="w-full p-2.5 rounded-xl border border-amber-300 text-xs font-sans text-right font-bold bg-white"
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-sans font-bold">PKR</span>
                        </div>
                        <span className="text-[11px] text-gray-500 mt-1 block font-urdu">
                          ڈیفالٹ رقم: 500 روپے۔ گاہک ایزی پیسہ یا بینک پر یہ رقم بھیجے گا، اور بقیہ بل پارسل ملنے پر کوریئر کو نقد دے گا۔
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-urdu text-rose-900 space-y-1">
                    <p className="font-bold">⚠️ کیش آن ڈلیوری فی الحال آف (بند) ہے</p>
                    <p className="text-[11px] text-rose-800">
                      ویب سائٹ پر چیک آؤٹ کے دوران کسٹمرز کو کیش آن ڈلیوری کا آپشن نظر نہیں آئے گا۔ ان کو مکمل بل کی رقم (Full Bill) پیشگی ایزی پیسہ یا آن لائن بینک ٹرانسفر کے ذریعے ادا کرنے کی ہدایت کی جائے گی۔
                    </p>
                  </div>
                )}
              </div>

              {/* لائیو وزٹرز و حالیہ آرڈرز پاپ اپ (Social Proof & Live Sales Notifications) */}
              <div className="bg-emerald-50/80 rounded-2xl p-5 border border-emerald-200 space-y-2">
                <div className="flex items-center gap-2 text-emerald-950 font-bold text-sm">
                  <Sparkles className="w-5 h-5 text-emerald-700 shrink-0" />
                  <span>لائیو وزٹرز و حالیہ خریداریوں کے نوٹیفکیشنز (Social Proof Popups)</span>
                </div>
                <p className="text-xs text-emerald-900 leading-relaxed font-urdu">
                  سکرین کے نیچے بائیں کونے میں چند سیکنڈ کے لیے آج کے وزٹرز اور حالیہ کنفرم شدہ آرڈرز کا خوبصورت پاپ اپ دکھاتا ہے جس سے کسٹمرز کا اعتماد اور سیلز میں اضافہ ہوتا ہے۔
                </p>

                <div className="flex items-center gap-3 pt-1">
                  <input
                    type="checkbox"
                    id="enableSocialProof"
                    checked={settingsForm.enableSocialProof ?? true}
                    onChange={(e) => setSettingsForm({ ...settingsForm, enableSocialProof: e.target.checked })}
                    className="w-5 h-5 text-emerald-600 rounded cursor-pointer accent-emerald-600"
                  />
                  <label htmlFor="enableSocialProof" className="text-xs font-bold text-gray-800 cursor-pointer select-none">
                    ویب سائٹ پر وزٹرز اور آرڈرز کے لائیو نوٹیفکیشنز فعال رکھیں
                  </label>
                </div>
              </div>

              {/* ادائیگی کے اکاؤنٹس (صرف ایزی پیسہ اور بینک) */}
              <div className="pt-2 space-y-3">
                <h4 className="font-bold text-xs text-gray-800 flex items-center gap-1.5 font-urdu">
                  <Banknote className="w-4 h-4 text-pansar-700" />
                  <span>وصولی کے اکاؤنٹس کی تفصیلات (EasyPaisa & Bank Accounts)</span>
                </h4>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 font-urdu">
                    ایزی پیسہ اکاؤنٹ نمبر و ٹائٹل (EasyPaisa Account)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.easypaisaAccount || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, easypaisaAccount: e.target.value })}
                    placeholder="03470005578 (سید عبدالوہاب شاہ)"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    بینک اکاؤنٹ کی تفصیلات (مختصر نام و ٹائٹل)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.bankDetails || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bankDetails: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    بینک IBAN نمبر (مثلاً: PK56UNIL0109000290123386)
                  </label>
                  <input
                    type="text"
                    value={settingsForm.bankIban || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bankIban: e.target.value })}
                    placeholder="PK56UNIL0109000290123386"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-mono dir-ltr text-left"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  کیو آر کوڈ تصویر کا پتہ (QR Code Image URL)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={settingsForm.bankQrUrl || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bankQrUrl: e.target.value })}
                    placeholder="/images/ubl-qr-code.jpg"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-mono dir-ltr text-left"
                  />
                  {settingsForm.bankQrUrl && (
                    <img 
                      src={settingsForm.bankQrUrl} 
                      alt="QR Preview" 
                      className="w-10 h-10 object-contain rounded-lg border border-gray-200 shrink-0 bg-white"
                    />
                  )}
                </div>
              </div>

              {/* سٹور رابطہ ای میل اور پتہ */}
              <div className="pt-2 border-t border-gray-100 space-y-3">
                <h4 className="font-bold text-xs text-gray-800 flex items-center gap-1.5 font-urdu">
                  <Mail className="w-4 h-4 text-pansar-700" />
                  <span>سٹور ای میل اور کلینک کا پتہ (Contact & Address)</span>
                </h4>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    آفیشل ای میل ایڈریس (Official Email)
                  </label>
                  <input
                    type="email"
                    value={settingsForm.email || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                    placeholder="info@yourstore.com"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      دواخانہ و پنسار کا پتہ (اردو)
                    </label>
                    <input
                      type="text"
                      value={settingsForm.addressUrdu || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, addressUrdu: e.target.value })}
                      placeholder="مرکزی پنسار مارکیٹ، پاکستان"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      پتہ (English Address)
                    </label>
                    <input
                      type="text"
                      value={settingsForm.addressEn || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, addressEn: e.target.value })}
                      placeholder="Main Pansar Market, Pakistan"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                    />
                  </div>
                </div>
              </div>
              {/* ڈیٹا بیک اپ اور بحالی (Backup & Restore) */}
              <div className="bg-emerald-50/60 rounded-2xl p-5 border border-emerald-200 space-y-3">
                <div className="flex items-center gap-2 text-emerald-950 font-bold text-sm font-urdu">
                  <FileDown className="w-4 h-4 text-emerald-700" />
                  <span>سٹور ڈیٹا بیک اپ و بحالی (One-Click Store Backup & Restore)</span>
                </div>
                <p className="text-xs text-emerald-900 leading-relaxed font-urdu">
                  اپنے تمام پروڈکٹس، کیٹیگریز، آرڈرز اور سیٹنگز کو ایک کلک میں اپنے کمپیوٹر پر محفوظ (Download) کریں، یا پرانے بیک اپ کو واپس ری سٹور کریں۔
                </p>

                {backupStatus && (
                  <div className={`p-3 rounded-xl text-xs font-urdu font-bold flex items-center gap-2 ${
                    backupStatus.type === 'success' ? 'bg-emerald-100 text-emerald-900' : 'bg-rose-100 text-rose-900'
                  }`}>
                    {backupStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>{backupStatus.text}</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={exportFullBackup}
                    className="py-2.5 px-4 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-sm transition flex items-center gap-2 cursor-pointer font-urdu"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>تمام ڈیٹا بیک اپ ڈاؤن لوڈ کریں (JSON)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => backupFileInputRef.current?.click()}
                    className="py-2.5 px-4 rounded-xl bg-white hover:bg-emerald-50 text-emerald-900 border border-emerald-300 font-bold text-xs shadow-sm transition flex items-center gap-2 cursor-pointer font-urdu"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>بیک اپ فائل اپلوڈ کریں (Restore)</span>
                  </button>

                  {/* Hidden file input for restore */}
                  <input
                    type="file"
                    ref={backupFileInputRef}
                    onChange={handleBackupFileChange}
                    accept=".json,application/json"
                    className="hidden"
                  />
                </div>
              </div>

              <div className="pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('کیا آپ تمام سیٹنگز اور پروڈکٹس کو ڈیفالٹ پر ری سیٹ کرنا چاہتے ہیں؟')) {
                      resetToDefaults();
                      setSettingsForm(settings);
                    }
                  }}
                  className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 font-urdu"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>سیٹنگز کو اصل حالت پر ری سیٹ کریں</span>
                </button>

                <div className="flex items-center gap-3">
                  {settingsSaved && (
                    <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      سیٹنگز محفوظ ہو گئیں!
                    </span>
                  )}
                  <button
                    type="submit"
                    className="py-2.5 px-6 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow transition"
                  >
                    سیٹنگز محفوظ کریں
                  </button>
                </div>
              </div>

            </form>
          </div>
        )}

        {/* TAB 4: CMS CONTENT MANAGEMENT */}
        {activeTab === 'cms' && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-200 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h3 className="font-bold text-base text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-700" />
                  <span>ویب سائٹ مواد اور تحریریں (Visual CMS Content Editor)</span>
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-urdu">
                  یہاں سے آپ ہیرو سیکشن، اوپر کا اعلان، حکیم صاحب کا تعارف، فوٹر اور سوال و جواب بغیر کسی کوڈ کے براہ راست ایڈٹ کر سکتے ہیں۔
                </p>
              </div>

              <div className="flex items-center gap-2">
                {cmsSaved && (
                  <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    تمام تبدیلیاں محفوظ ہو گئیں!
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleSaveCms}
                  className="py-2.5 px-6 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-md transition flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>تبدیلیاں محفوظ کریں (Save CMS)</span>
                </button>
              </div>
            </div>

            {/* CMS Sub-Tabs Navigation */}
            <div className="flex flex-wrap gap-2 border-b border-gray-100 pb-3">
              {[
                { id: 'hero', label: 'ہیرو سیکشن و بٹنز (Hero)', icon: Layout },
                { id: 'announcement', label: 'ٹاپ اعلان (Announcement)', icon: Sparkles },
                { id: 'sections', label: 'سیکشنز ہیڈنگز (Sections)', icon: Type },
                { id: 'hakim', label: 'حکیم صاحب پروفائل (Hakim Bio)', icon: User },
                { id: 'mizaj', label: 'طبی تشخیصی گائیڈ و چار مزاج (Mizaj Guide)', icon: HeartPulse },
                { id: 'badges', label: 'اعتماد کے بیجز (Trust Badges)', icon: ShieldCheck },
                { id: 'faqs', label: 'عمومی سوال و جواب (FAQs)', icon: HelpCircle },
                { id: 'footer', label: 'فوٹر و ڈسکلیمر (Footer)', icon: Layers },
                { id: 'social', label: 'سوشل میڈیا روابط (Social)', icon: Share2 },
              ].map(sec => {
                const Icon = sec.icon;
                return (
                  <button
                    key={sec.id}
                    type="button"
                    onClick={() => setActiveCmsSection(sec.id)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeCmsSection === sec.id
                        ? 'bg-amber-100 text-amber-900 border border-amber-300 shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{sec.label}</span>
                  </button>
                );
              })}
            </div>

            {/* SECTION 1: HERO SECTION */}
            {activeCmsSection === 'hero' && (
              <div className="space-y-4">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  یہ تحریریں اور تصویر ویب سائٹ کے سب سے اوپر ہیرو سیکشن (Hero Banner) میں نمایاں نظر آتی ہیں۔
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      بڑی مرکزی ہیڈنگ (اردو)
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.hero?.headlineUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hero: { ...(cmsForm?.hero || {}), headlineUrdu: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      placeholder="خالص جڑی بوٹیاں اور طبِ پاکستانی (قانون مفرد اعضاء) کا مرکز"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      بڑی مرکزی ہیڈنگ (انگریزی - English)
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.hero?.headlineEn || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hero: { ...(cmsForm?.hero || {}), headlineEn: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                      placeholder="Pure Botanicals & Authentic Tibb-e-Pakistani (Qanoon-e-Mufrad Aza)"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      ذیلی تعارفی پیراگراف (اردو)
                    </label>
                    <textarea
                      rows="3"
                      value={cmsForm?.hero?.subheadingUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hero: { ...(cmsForm?.hero || {}), subheadingUrdu: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      ذیلی تعارفی پیراگراف (انگریزی - English)
                    </label>
                    <textarea
                      rows="3"
                      value={cmsForm?.hero?.subheadingEn || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hero: { ...(cmsForm?.hero || {}), subheadingEn: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                    />
                  </div>

                  {/* Hero Card Image */}
                  <div className="pt-2 border-t border-gray-100">
                    <ImageUploadField
                      label="🖼️ ہیرو بینر کی بڑی تصویر (Hero Banner Image)"
                      value={cmsForm?.hero?.heroImageUrl || ''}
                      onChange={(val) => setCmsForm({
                        ...cmsForm,
                        hero: { ...(cmsForm?.hero || {}), heroImageUrl: val }
                      })}
                      maxWidth={800}
                      helperText="ہوم پیج کے مین ہیرو بینر کے لیے خوبصورت، ہائی کوالٹی جڑی بوٹیوں یا بینر کی تصویر منتخب کریں۔"
                    />
                  </div>

                  {/* 3 Action Button Labels */}
                  <div className="pt-2 border-t border-gray-100">
                    <h5 className="font-bold text-xs text-gray-800 mb-2">ہیرو سیکشن کے 3 ایکشن بٹنز کے الفاظ (اردو):</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-1">بٹن 1 (شاپ بٹن):</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.btn1TextUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), btn1TextUrdu: e.target.value }
                          })}
                          placeholder="تمام ادویات و مصنوعات دیکھیں"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-1">بٹن 2 (واٹس ایپ بٹن):</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.btn2TextUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), btn2TextUrdu: e.target.value }
                          })}
                          placeholder="حکیم صاحب سے طبی مشورہ"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-1">بٹن 3 (مزاج گائیڈ بٹن):</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.btn3TextUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), btn3TextUrdu: e.target.value }
                          })}
                          placeholder="مزاج اور امراض گائیڈ"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 3 Metrics */}
                  <div className="pt-2 border-t border-gray-100">
                    <h5 className="font-bold text-xs text-gray-800 mb-3">ہیرو سیکشن کے 3 کاؤنٹرز / میٹرکس:</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                        <label className="block text-[11px] font-bold text-gray-600">میٹرک 1:</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat1Number || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat1Number: e.target.value }
                          })}
                          placeholder="مثلاً 100%"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-bold"
                        />
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat1LabelUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat1LabelUrdu: e.target.value }
                          })}
                          placeholder="لیبل: خالص قدرتی اجزاء"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-urdu"
                        />
                      </div>

                      <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                        <label className="block text-[11px] font-bold text-gray-600">میٹرک 2:</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat2Number || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat2Number: e.target.value }
                          })}
                          placeholder="مثلاً 5,000+"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-bold"
                        />
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat2LabelUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat2LabelUrdu: e.target.value }
                          })}
                          placeholder="لیبل: مطمئن مریض"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-urdu"
                        />
                      </div>

                      <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2">
                        <label className="block text-[11px] font-bold text-gray-600">میٹرک 3:</label>
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat3Number || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat3Number: e.target.value }
                          })}
                          placeholder="مثلاً 24/7"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-bold"
                        />
                        <input
                          type="text"
                          value={cmsForm?.hero?.stat3LabelUrdu || ''}
                          onChange={(e) => setCmsForm({
                            ...cmsForm,
                            hero: { ...(cmsForm?.hero || {}), stat3LabelUrdu: e.target.value }
                          })}
                          placeholder="لیبل: طبی رہنمائی"
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs text-center font-urdu"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 2: TOP ANNOUNCEMENT BANNER */}
            {activeCmsSection === 'announcement' && (
              <div className="space-y-4 max-w-2xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  یہ پیغام ویب سائٹ کی سب سے اوپر والی باریک سبز پٹی پر ہر صفحے پر نظر آتا ہے۔
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    اوپر کی پٹی کا اعلان (اردو)
                  </label>
                  <textarea
                    rows="2"
                    value={cmsForm?.announcement?.textUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      announcement: { ...cmsForm.announcement, textUrdu: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    placeholder="پورے پاکستان میں کیش آن ڈلیوری کی سہولت | 5,000 روپے سے زائد پر مفت ڈلیوری!"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    اوپر کی پٹی کا اعلان (انگریزی - English)
                  </label>
                  <textarea
                    rows="2"
                    value={cmsForm?.announcement?.textEn || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      announcement: { ...cmsForm.announcement, textEn: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                    placeholder="Cash on Delivery Across Pakistan | Free Shipping on Orders Over Rs. 5,000!"
                  />
                </div>
              </div>
            )}

            {/* SECTION: CATEGORIES & FEATURED PRODUCTS TITLES */}
            {activeCmsSection === 'sections' && (
              <div className="space-y-6 max-w-3xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  یہاں سے آپ ہوم پیج پر اقسام ادویات (Categories) اور سب سے زیادہ خریدی جانے والی ادویات (Featured Products) کے عنوانات اور ذیلی تفصیلات تبدیل کر سکتے ہیں۔
                </div>

                {/* Categories Titles */}
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                  <h4 className="font-bold text-xs text-pansar-900 flex items-center gap-1.5 border-b border-gray-200 pb-2">
                    <Type className="w-4 h-4 text-emerald-700" />
                    <span>اقسام ادویات سیکشن (Categories Section Headers):</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        اوپر کا چھوٹا بیج/ٹیگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.sectionTitles?.categoriesTagUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          sectionTitles: { ...cmsForm.sectionTitles, categoriesTagUrdu: e.target.value }
                        })}
                        placeholder="اقسام ادویات و پنسار"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        بڑی مرکزی ہیڈنگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.sectionTitles?.categoriesTitleUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          sectionTitles: { ...cmsForm.sectionTitles, categoriesTitleUrdu: e.target.value }
                        })}
                        placeholder="قانون مفرد اعضاء و ہربل کیٹیگریز"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-700 mb-1">
                      ذیلی وضاحتی جملہ (اردو):
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.sectionTitles?.categoriesSubtitleUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        sectionTitles: { ...cmsForm.sectionTitles, categoriesSubtitleUrdu: e.target.value }
                      })}
                      placeholder="اعضائے رئیسہ (اعصاب، عضلات، غدد) کے لیے مستند و خالص نباتاتی مرکبات"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                </div>

                {/* Featured Products Titles */}
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                  <h4 className="font-bold text-xs text-pansar-900 flex items-center gap-1.5 border-b border-gray-200 pb-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span>نمایاں و مقبول ادویات سیکشن (Featured Products Headers):</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        اوپر کا چھوٹا بیج/ٹیگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.sectionTitles?.featuredTagUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          sectionTitles: { ...cmsForm.sectionTitles, featuredTagUrdu: e.target.value }
                        })}
                        placeholder="انتخابِ خاص و مایہ ناز مرکبات"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        بڑی مرکزی ہیڈنگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.sectionTitles?.featuredTitleUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          sectionTitles: { ...cmsForm.sectionTitles, featuredTitleUrdu: e.target.value }
                        })}
                        placeholder="سب سے زیادہ خریدی جانے والی ادویات"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-700 mb-1">
                      ذیلی وضاحتی جملہ (اردو):
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.sectionTitles?.featuredSubtitleUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        sectionTitles: { ...cmsForm.sectionTitles, featuredSubtitleUrdu: e.target.value }
                      })}
                      placeholder="حکیم مولانا سید عبدالوہاب شاہ شیرازی کے مصدقہ اور آزمودہ قدرتی نسخہ جات"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 3: HAKIM PROFILE */}
            {activeCmsSection === 'hakim' && (
              <div className="space-y-4 max-w-3xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  حکیم صاحب کے بینر میں نمایاں ہونے والی اسناد، القاب، تصویر اور تفصیلی سوانح حیات۔
                </div>

                {/* Hakim Photo */}
                <ImageUploadField
                  label="🖼️ حکیم صاحب کی تصویر (Hakim Profile Photo)"
                  value={cmsForm?.hakimProfile?.photoUrl || ''}
                  onChange={(val) => setCmsForm({
                    ...cmsForm,
                    hakimProfile: { ...cmsForm.hakimProfile, photoUrl: val }
                  })}
                  maxWidth={600}
                  previewShape="square"
                  helperText="حکیم صاحب کی باوقار تصویر اپلوڈ کریں جو ہوم پیج کے پروفائل کارڈ میں ظاہر ہوگی۔"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      طبی اسناد / ڈگری کا بیج
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.hakimProfile?.qualificationUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hakimProfile: { ...cmsForm.hakimProfile, qualificationUrdu: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      placeholder="فاضل طب والجراحت (FTJ)"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      انتظامی عہدہ / شعبہ
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.hakimProfile?.designationUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hakimProfile: { ...cmsForm.hakimProfile, designationUrdu: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      placeholder="طبی سرپرستی و ادارت"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    حکیم صاحب کا تفصیلی تعارف و فلسفہ (اردو)
                  </label>
                  <textarea
                    rows="4"
                    value={cmsForm?.hakimProfile?.bioUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      hakimProfile: { ...cmsForm.hakimProfile, bioUrdu: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu leading-relaxed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    حکیم صاحب کا تعارف (انگریزی - English)
                  </label>
                  <textarea
                    rows="3"
                    value={cmsForm?.hakimProfile?.bioEn || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      hakimProfile: { ...cmsForm.hakimProfile, bioEn: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr leading-relaxed"
                  />
                </div>

                {/* 3 Consultation Features & CTA Button */}
                <div className="pt-3 border-t border-gray-100 space-y-3">
                  <h5 className="font-bold text-xs text-gray-800">حکیم صاحب بینر کی 3 خصوصیات (بلٹ پوائنٹس):</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">پوائنٹ 1:</label>
                      <input
                        type="text"
                        value={cmsForm?.hakimProfile?.feature1Urdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          hakimProfile: { ...cmsForm.hakimProfile, feature1Urdu: e.target.value }
                        })}
                        placeholder="نبض شناسی و مزاجی تشخیص"
                        className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">پوائنٹ 2:</label>
                      <input
                        type="text"
                        value={cmsForm?.hakimProfile?.feature2Urdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          hakimProfile: { ...cmsForm.hakimProfile, feature2Urdu: e.target.value }
                        })}
                        placeholder="خالص نسخہ جات کی دستی تیاری"
                        className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-600 mb-1">پوائنٹ 3:</label>
                      <input
                        type="text"
                        value={cmsForm?.hakimProfile?.feature3Urdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          hakimProfile: { ...cmsForm.hakimProfile, feature3Urdu: e.target.value }
                        })}
                        placeholder="آن لائن مفت واٹس ایپ مشورہ"
                        className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      واٹس ایپ رابطہ بٹن کی تحریر (CTA Button Text):
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.hakimProfile?.ctaButtonUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        hakimProfile: { ...cmsForm.hakimProfile, ctaButtonUrdu: e.target.value }
                      })}
                      placeholder="واٹس ایپ پر حکیم صاحب سے براہِ راست رابطہ"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SECTION: TIBBI DIAGNOSIS GUIDE & 6 MIZAJ */}
            {activeCmsSection === 'mizaj' && (
              <div className="space-y-6 max-w-3xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  طبِ پاکستانی (قانون مفرد اعضاء از صابر ملتانیؒ) تشخیصی رہنمائی کے عنوانات، 6 مزاج، علامات، نبض اور اصولِ علاج کو یہاں سے بآسانی تبدیل کریں۔
                </div>

                {/* Section Header */}
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                  <h4 className="font-bold text-xs text-pansar-900 flex items-center gap-1.5 border-b border-gray-200 pb-2">
                    <HeartPulse className="w-4 h-4 text-rose-600" />
                    <span>سیکشن کے عمومی عنوانات:</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        چھوٹا بیج / ٹیگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.tibbiDiagnosis?.sectionTagUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          tibbiDiagnosis: { ...cmsForm.tibbiDiagnosis, sectionTagUrdu: e.target.value }
                        })}
                        placeholder="قانون مفرد اعضاء (طبِ پاکستانی) تشخیصی گائیڈ"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        بڑی مرکزی ہیڈنگ (اردو):
                      </label>
                      <input
                        type="text"
                        value={cmsForm?.tibbiDiagnosis?.titleUrdu || ''}
                        onChange={(e) => setCmsForm({
                          ...cmsForm,
                          tibbiDiagnosis: { ...cmsForm.tibbiDiagnosis, titleUrdu: e.target.value }
                        })}
                        placeholder="اعضائے رئیسہ (اعصاب، عضلات، غدد) اور نبض کے مطابق دوا تلاش کریں"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-700 mb-1">
                      تفصیلی وضاحتی جملہ (اردو):
                    </label>
                    <textarea
                      rows="2"
                      value={cmsForm?.tibbiDiagnosis?.descUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        tibbiDiagnosis: { ...cmsForm.tibbiDiagnosis, descUrdu: e.target.value }
                      })}
                      placeholder="طبِ پاکستانی میں بیماری کا سبب تینوں مفرد اعضاء (اعصاب، عضلات، غدد) میں تحریک، تسکین یا تحلیل کا بگاڑ ہے..."
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                </div>

                {/* 6 Mizaj Archetypes */}
                <div className="space-y-4">
                  <h4 className="font-bold text-xs text-pansar-900 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span>قانون مفرد اعضاء کے 6 مزاجوں کی علامات، نبض و اصولِ علاج:</span>
                  </h4>

                  {(cmsForm?.tibbiDiagnosis?.archetypes || []).map((arch, idx) => (
                    <div key={arch.id || idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                        <span className="font-bold text-xs text-pansar-950 font-title">
                          مزاج {idx + 1}: {arch.nameUrdu}
                        </span>
                        <span className="text-[11px] font-mono text-gray-400">ID: {arch.id}</span>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          مزاج کا نام (اردو):
                        </label>
                        <input
                          type="text"
                          value={arch.nameUrdu || ''}
                          onChange={(e) => handleUpdateArchetype(idx, 'nameUrdu', e.target.value)}
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu font-bold"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          عام علامات و کیفیات (اردو):
                        </label>
                        <textarea
                          rows="2"
                          value={arch.symptomsUrdu || ''}
                          onChange={(e) => handleUpdateArchetype(idx, 'symptomsUrdu', e.target.value)}
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu leading-relaxed"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-emerald-800 mb-1">
                          طبی اصولِ علاج و مفید غذائیں/ادویات (اردو):
                        </label>
                        <textarea
                          rows="2"
                          value={arch.remedyPrincipleUrdu || ''}
                          onChange={(e) => handleUpdateArchetype(idx, 'remedyPrincipleUrdu', e.target.value)}
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu leading-relaxed"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 4: TRUST BADGES */}
            {activeCmsSection === 'badges' && (
              <div className="space-y-4">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  ہوم پیج پر ادویات کے اوپر نظر آنے والے 4 خصوصیات / اعتماد کے بیجز۔
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(cmsForm?.trustBadges || []).map((badge, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-2">
                      <div className="font-bold text-xs text-pansar-900">بیج نمبر {idx + 1}:</div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5">عنوان (اردو):</label>
                        <input
                          type="text"
                          value={badge.titleUrdu || ''}
                          onChange={(e) => handleUpdateBadge(idx, 'titleUrdu', e.target.value)}
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5">تفصیل (اردو):</label>
                        <input
                          type="text"
                          value={badge.descUrdu || ''}
                          onChange={(e) => handleUpdateBadge(idx, 'descUrdu', e.target.value)}
                          className="w-full p-2 rounded-lg border border-gray-200 text-xs font-urdu"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 5: FAQS (QUESTIONS & ANSWERS) */}
            {activeCmsSection === 'faqs' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200">
                  <div className="text-xs text-emerald-900 font-urdu">
                    یہاں سے آپ سوالات و جوابات کو تبدیل، نیا سوال شامل یا پرانا ڈیلیٹ کر سکتے ہیں۔
                  </div>
                  <button
                    type="button"
                    onClick={handleAddFaq}
                    className="py-1.5 px-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs flex items-center gap-1 shadow-sm cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>نیا سوال شامل کریں (Add FAQ)</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {(cmsForm?.faqs || []).map((faq, idx) => (
                    <div key={faq.id || idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                        <span className="font-bold text-xs text-pansar-900">سوال نمبر {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteFaq(faq.id)}
                          className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 font-bold cursor-pointer"
                          title="یہ سوال ڈیلیٹ کریں"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>حذف کریں</span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-gray-700 mb-1">سوال (اردو):</label>
                          <input
                            type="text"
                            value={faq.qUrdu || ''}
                            onChange={(e) => handleUpdateFaq(faq.id, 'qUrdu', e.target.value)}
                            className="w-full p-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-gray-700 mb-1">سوال (انگریزی):</label>
                          <input
                            type="text"
                            value={faq.qEn || ''}
                            onChange={(e) => handleUpdateFaq(faq.id, 'qEn', e.target.value)}
                            className="w-full p-2 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-gray-700 mb-1">جواب (اردو):</label>
                          <textarea
                            rows="2"
                            value={faq.aUrdu || ''}
                            onChange={(e) => handleUpdateFaq(faq.id, 'aUrdu', e.target.value)}
                            className="w-full p-2 rounded-xl border border-gray-200 text-xs font-urdu"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-gray-700 mb-1">جواب (انگریزی):</label>
                          <textarea
                            rows="2"
                            value={faq.aEn || ''}
                            onChange={(e) => handleUpdateFaq(faq.id, 'aEn', e.target.value)}
                            className="w-full p-2 rounded-xl border border-gray-200 text-xs font-sans text-left dir-ltr"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 6: FOOTER & CLINIC INFO */}
            {activeCmsSection === 'footer' && (
              <div className="space-y-4 max-w-3xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  ویب سائٹ کے نچلے حصے (Footer) میں دکھائی جانے والی تحریریں، ترسیل کی تفصیل، ڈسکلیمر اور مطب کے اوقاتِ کار۔
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    طبی سرپرستی کا مختصر نوٹ (اردو)
                  </label>
                  <textarea
                    rows="2"
                    value={cmsForm?.footer?.aboutUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      footer: { ...cmsForm.footer, aboutUrdu: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      مطب / کلینک کے اوقاتِ کار (اردو)
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.footer?.clinicTimingsUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        footer: { ...cmsForm.footer, clinicTimingsUrdu: e.target.value }
                      })}
                      placeholder="روزانہ صبح 10:00 بجے تا شام 8:00 بجے"
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      کلینک کا پتہ (اردو)
                    </label>
                    <input
                      type="text"
                      value={cmsForm?.footer?.clinicAddressUrdu || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        footer: { ...cmsForm.footer, clinicAddressUrdu: e.target.value }
                      })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                    />
                  </div>
                </div>

                {/* Delivery Information Column Note */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    📦 ترسیل و کوریئر کی وضاحت (Delivery Column Note):
                  </label>
                  <textarea
                    rows="2"
                    value={cmsForm?.footer?.deliveryColTextUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      footer: { ...cmsForm.footer, deliveryColTextUrdu: e.target.value }
                    })}
                    placeholder="ہم پورے پاکستان کے تمام شہروں، قصبوں اور دیہاتوں میں بذریعہ کوریئر بحفاظت پارسل روانہ کرتے ہیں۔"
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>

                {/* Medical Disclaimer */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    ⚖️ طبی انتباہ و قانونی ڈسکلیمر (Medical Disclaimer):
                  </label>
                  <textarea
                    rows="3"
                    value={cmsForm?.footer?.disclaimerUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      footer: { ...cmsForm.footer, disclaimerUrdu: e.target.value }
                    })}
                    placeholder="طبی انتباہ و ڈسکلیمر: ہماری ادویات اور جڑی بوٹیاں طبِ پاکستانی (قانون مفرد اعضاء) کے اصولوں پر..."
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu leading-relaxed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    کاپی رائٹ کی تحریر (Copyright)
                  </label>
                  <input
                    type="text"
                    value={cmsForm?.footer?.copyrightUrdu || ''}
                    onChange={(e) => setCmsForm({
                      ...cmsForm,
                      footer: { ...cmsForm.footer, copyrightUrdu: e.target.value }
                    })}
                    className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu"
                  />
                </div>
              </div>
            )}

            {/* SECTION: SOCIAL MEDIA & EXTERNAL LINKS */}
            {activeCmsSection === 'social' && (
              <div className="space-y-4 max-w-3xl">
                <div className="bg-emerald-50/70 p-3.5 rounded-2xl border border-emerald-200 text-xs text-emerald-900 font-urdu">
                  اپنے آفیشل فیس بک، یوٹیوب، انسٹاگرام اور مطب کے گوگل میپس لوکیشن کا لنک یہاں درج کریں۔
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-200 space-y-1.5">
                    <label className="block text-xs font-bold text-gray-800">
                      فیس بک پیج لنک (Facebook URL):
                    </label>
                    <input
                      type="url"
                      value={cmsForm?.socialLinks?.facebook || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        socialLinks: { ...cmsForm.socialLinks, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/..."
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-left"
                    />
                  </div>

                  <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-200 space-y-1.5">
                    <label className="block text-xs font-bold text-gray-800">
                      یوٹیوب چینل لنک (YouTube URL):
                    </label>
                    <input
                      type="url"
                      value={cmsForm?.socialLinks?.youtube || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        socialLinks: { ...cmsForm.socialLinks, youtube: e.target.value }
                      })}
                      placeholder="https://youtube.com/@..."
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-left"
                    />
                  </div>

                  <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-200 space-y-1.5">
                    <label className="block text-xs font-bold text-gray-800">
                      انسٹاگرام پروفائل لنک (Instagram URL):
                    </label>
                    <input
                      type="url"
                      value={cmsForm?.socialLinks?.instagram || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        socialLinks: { ...cmsForm.socialLinks, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/..."
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-left"
                    />
                  </div>

                  <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-200 space-y-1.5">
                    <label className="block text-xs font-bold text-gray-800">
                      گوگل میپس لوکیشن لنک (Google Maps URL):
                    </label>
                    <input
                      type="url"
                      value={cmsForm?.socialLinks?.googleMaps || ''}
                      onChange={(e) => setCmsForm({
                        ...cmsForm,
                        socialLinks: { ...cmsForm.socialLinks, googleMaps: e.target.value }
                      })}
                      placeholder="https://maps.google.com/..."
                      className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans dir-ltr text-left"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Actions for CMS */}
            <div className="pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('کیا آپ تمام تحریروں کو اصل ڈیفالٹ حالت پر ری سیٹ کرنا چاہتے ہیں؟')) {
                    resetCmsToDefaults();
                    setCmsForm(DEFAULT_CMS_CONTENT);
                  }
                }}
                className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 font-urdu cursor-pointer"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>تمام تحریروں کو اصل ڈیفالٹ پر ری سیٹ کریں</span>
              </button>

              <div className="flex items-center gap-3">
                {cmsSaved && (
                  <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    تمام تبدیلیاں محفوظ ہو گئیں!
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleSaveCms}
                  className="py-3 px-8 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-md transition flex items-center gap-2 cursor-pointer"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>تبدیلیاں محفوظ کریں (Save CMS)</span>
                </button>
              </div>
            </div>

          </div>
        )}

        {/* TAB: BLOGS & HEALTH ARTICLES */}
        {activeTab === 'blogs' && (
          <BlogManager />
        )}
        {activeTab === 'pages' && (
          <PageManager />
        )}
        {activeTab === 'media' && (
          <MediaManager />
        )}

        {/* TAB: BACKUP & GITHUB SYNC CENTER */}
        {activeTab === 'backup' && (
          <div className="space-y-6 animate-fade-in font-urdu">
            
            {/* Top Alert / Status message */}
            {backupStatus && (
              <div className={`p-4 rounded-2xl flex items-center justify-between text-xs font-bold border shadow-md ${
                backupStatus.type === 'error'
                  ? 'bg-rose-50 text-rose-900 border-rose-200'
                  : 'bg-emerald-50 text-emerald-900 border-emerald-200'
              }`}>
                <div className="flex items-center gap-2">
                  {backupStatus.type === 'error' ? <AlertCircle className="w-5 h-5 text-rose-600" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                  <span>{backupStatus.msg}</span>
                </div>
                <button onClick={() => setBackupStatus(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Header Banner */}
            <div className="bg-gradient-to-l from-pansar-900 via-pansar-800 to-emerald-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-emerald-700/40 relative overflow-hidden">
              <div className="relative z-10 max-w-3xl space-y-2">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold font-title border border-amber-400/30">
                  <ShieldCheck className="w-4 h-4 text-amber-300" />
                  <span>100% محفوظ و مستقل ڈیٹا پروٹیکشن سنٹر</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-black font-title text-white">
                  ڈیٹا بیک اپ، ری اسٹور اور گٹ ہب سنک سنٹر
                </h2>
                <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed">
                  یہاں سے آپ اپنے سٹور کی تمام پروڈکٹس، کیٹیگریز، بلاگز اور سیٹنگز کا فوری بیک اپ اپنے کمپیوٹر پر ڈاؤن لوڈ کر سکتے ہیں اور گٹ ہب (GitHub) پر پش کرنے کے لیے تیار کوڈ فائلیں حاصل کر سکتے ہیں تاکہ ویب سائٹ اپڈیٹ کرنے پر کوئی بھی چیز کبھی ضائع نہ ہو۔
                </p>
              </div>
            </div>

            {/* Grid of Backup Tools */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* CARD 1: Full Store JSON Backup & Restore */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 space-y-5 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center">
                      <Download className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-gray-900 font-title">
                        1. مکمل سٹور بیک اپ (JSON Backup)
                      </h3>
                      <p className="text-xs text-gray-500">
                        تمام پروڈکٹس، کیٹیگریز، بلاگز، آرڈرز اور سیٹنگز کا 1-کلک بیک اپ
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
                    یہ فائل آپ کے پورے سٹور کے تمام ڈیٹا کا مکمل ریکارڈ ہے۔ اگر آپ براؤزر کی ہسٹری صاف کر دیں، یا کسی نئے لیپ ٹاپ/موبائل پر کام کر رہے ہوں، تو صرف اس فائل کو اپلوڈ کرنے سے سارا سٹور واپس بحال ہو جائے گا۔
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-3">
                  {/* Export Button */}
                  <button
                    type="button"
                    onClick={() => {
                      exportFullBackup();
                      setBackupStatus({ type: 'success', msg: 'پورے سٹور کا بیک اپ کامیابی سے ڈاؤن لوڈ ہو گیا ہے!' });
                    }}
                    className="w-full py-3 px-4 rounded-2xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Download className="w-4 h-4 text-amber-300" />
                    <span>مکمل بیک اپ فائل ڈاؤن لوڈ کریں (Download Backup .json)</span>
                  </button>

                  {/* Hidden File Input for Import */}
                  <input
                    type="file"
                    ref={backupFileInputRef}
                    accept=".json"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        try {
                          const parsed = JSON.parse(event.target.result);
                          const res = importFullBackup(parsed);
                          if (res.success) {
                            setBackupStatus({ type: 'success', msg: 'بیک اپ کامیابی سے بحال کر دیا گیا ہے! تمام پروڈکٹس و سیٹنگز اپڈیٹ ہو گئیں۔' });
                          } else {
                            setBackupStatus({ type: 'error', msg: `بیک اپ فائل پڑھنے میں خرابی: ${res.error}` });
                          }
                        } catch (err) {
                          setBackupStatus({ type: 'error', msg: 'منتخب کردہ فائل درست JSON فارمیٹ میں نہیں ہے۔' });
                        }
                      };
                      reader.readAsText(file);
                      e.target.value = '';
                    }}
                  />

                  {/* Import Button */}
                  <button
                    type="button"
                    onClick={() => backupFileInputRef.current?.click()}
                    className="w-full py-3 px-4 rounded-2xl bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold text-xs border border-emerald-200 transition flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Upload className="w-4 h-4 text-emerald-700" />
                    <span>بیک اپ فائل سے ڈیٹا بحال کریں (Restore from Backup)</span>
                  </button>
                </div>
              </div>

              {/* CARD 2: GitHub Codebase Sync Tools */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 space-y-5 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-800 flex items-center justify-center">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-gray-900 font-title">
                        2. گٹ ہب کوڈ سنک (GitHub Codebase Sync)
                      </h3>
                      <p className="text-xs text-gray-500">
                        پروڈکٹس کو سورس کوڈ میں مستقل محفوظ کرنے کا طریقہ
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 leading-relaxed bg-blue-50/60 p-3.5 rounded-2xl border border-blue-100">
                    گٹ ہب پر ویب سائٹ پش کرنے سے پہلے نیچے دیا گیا بٹن دبا کر <code className="bg-white px-1 py-0.5 rounded text-blue-900 font-mono text-[11px]">productsData.js</code> ڈاؤن لوڈ کریں اور اپنے پروجیکٹ کے فولڈر <code className="bg-white px-1 py-0.5 rounded text-blue-900 font-mono text-[11px]">src/data/productsData.js</code> میں رکھ دیں۔ اس سے آپ کی تمام نئی پروڈکٹس ہمیشہ کے لیے مستقل ہو جائیں گی!
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2.5">
                  {/* Download productsData.js */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        downloadProductsJsFile();
                        setBackupStatus({ type: 'success', msg: 'productsData.js کامیابی سے ڈاؤن لوڈ ہو گئی ہے! اسے src/data/ میں رکھیں۔' });
                      }}
                      className="flex-1 py-2.5 px-4 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                    >
                      <Download className="w-4 h-4 text-amber-300" />
                      <span>productsData.js ڈاؤن لوڈ کریں ({products.length} پروڈکٹس)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const code = getProductsJsCode();
                        navigator.clipboard.writeText(code);
                        setCopiedProductCode(true);
                        setTimeout(() => setCopiedProductCode(false), 2500);
                      }}
                      className="py-2.5 px-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs transition flex items-center gap-1 cursor-pointer border border-gray-200"
                      title="کوڈ کلپ بورڈ پر کاپی کریں"
                    >
                      {copiedProductCode ? <Check className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-gray-600" />}
                      <span>{copiedProductCode ? 'کاپی ہو گیا!' : 'کوڈ کاپی'}</span>
                    </button>
                  </div>

                  {/* Download categoriesData.js */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        downloadCategoriesJsFile();
                        setBackupStatus({ type: 'success', msg: 'categoriesData.js کامیابی سے ڈاؤن لوڈ ہو گئی ہے! اسے src/data/ میں رکھیں۔' });
                      }}
                      className="flex-1 py-2.5 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer border border-gray-200"
                    >
                      <Download className="w-4 h-4 text-emerald-700" />
                      <span>categoriesData.js ڈاؤن لوڈ کریں ({categories.length} کیٹیگریز)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const code = getCategoriesJsCode();
                        navigator.clipboard.writeText(code);
                        setCopiedCatCode(true);
                        setTimeout(() => setCopiedCatCode(false), 2500);
                      }}
                      className="py-2.5 px-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs transition flex items-center gap-1 cursor-pointer border border-gray-200"
                      title="کیٹیگریز کا کوڈ کاپی کریں"
                    >
                      {copiedCatCode ? <Check className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-gray-600" />}
                      <span>{copiedCatCode ? 'کاپی ہو گیا!' : 'کوڈ کاپی'}</span>
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {/* CARD 3: Step-by-Step GitHub Guide */}
            <div className="bg-amber-50/70 rounded-3xl p-6 border border-amber-200/80 space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm text-amber-950 font-title">
                <HelpCircle className="w-5 h-5 text-amber-700" />
                <span>گٹ ہب پر پروڈکٹس کو مستقل رکھنے کا آسان طریقہ (3 Steps Guide)</span>
              </div>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-amber-900 leading-relaxed">
                <li>ایڈمن پینل سے تمام نئی ادویات شامل کرنے کے بعد <strong>"productsData.js ڈاؤن لوڈ کریں"</strong> پر کلک کریں۔</li>
                <li>ڈاؤن لوڈ ہونے والی فائل کو اپنے پروجیکٹ فولڈر <strong>`src/data/productsData.js`</strong> کی جگہ پر پیسٹ (Replace) کر دیں۔</li>
                <li>اب ٹرمینل میں <strong>`git add .`</strong>، <strong>`git commit -m "Update products"`</strong> اور <strong>`git push`</strong> کر دیں۔ آپ کی پروڈکٹس ہمیشہ کے لیے گٹ ہب میں محفوظ ہو جائیں گی!</li>
              </ol>
            </div>

          </div>
        )}

        {/* TAB: DEDICATED ADMIN SECURITY & 2FA */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            
            {/* Header Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xs border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0 shadow-xs">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 font-urdu">
                      ایڈمن سیکیورٹی، 2FA اور رسائی کنٹرول
                    </h2>
                    <p className="text-xs text-gray-500 font-urdu mt-0.5">
                      خفیہ ایڈمن یو آر ایل، ایڈمن پاس ورڈ، گوگل اتھینٹیکیٹر 2FA اور ریکوری ای میلز کا مکمل انتظام
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  {settingsSaved && (
                    <span className="text-xs text-emerald-700 font-bold flex items-center gap-1 font-urdu bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4" />
                      سیٹنگز محفوظ ہو گئیں!
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    className="py-2.5 px-6 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition font-urdu cursor-pointer flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    <span>سیکیورٹی سیٹنگز محفوظ کریں</span>
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-6">
                
                {/* SECTION 1: SECRET ADMIN URL */}
                <div className="bg-linear-to-r from-slate-900 to-slate-800 rounded-2xl p-5 sm:p-6 text-white border border-slate-700 shadow-md space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-emerald-400">
                        <Lock className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-white font-urdu">
                          الف) خفیہ ایڈمن لاگ ان ایڈریس (Secret Custom Admin URL)
                        </h3>
                        <p className="text-xs text-slate-300 font-urdu mt-0.5">
                          عام یو آر ایل (/admin یا /login) مکمل بلاک ہیں۔ صرف آپ کے اس خفیہ لنک سے ایڈمن پینل لاگ ان کھلے گا۔
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Secret URL Display and Copy */}
                  <div className="bg-slate-950/70 rounded-xl p-3 sm:p-4 border border-slate-700 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-xs text-slate-400 font-urdu shrink-0">آپ کا خفیہ لنک:</span>
                      <span className="font-mono text-xs sm:text-sm text-emerald-400 font-bold truncate select-all dir-ltr text-left">
                        {typeof window !== 'undefined' 
                          ? `${window.location.origin}/${settingsForm.adminSecretSlug || 'sherazi-5083'}`
                          : `https://himalayanpansar.com/${settingsForm.adminSecretSlug || 'sherazi-5083'}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const fullUrl = typeof window !== 'undefined'
                          ? `${window.location.origin}/${settingsForm.adminSecretSlug || 'sherazi-5083'}`
                          : `https://himalayanpansar.com/${settingsForm.adminSecretSlug || 'sherazi-5083'}`;
                        navigator.clipboard.writeText(fullUrl);
                        setCopiedUrlToast(true);
                        setTimeout(() => setCopiedUrlToast(false), 2500);
                      }}
                      className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shrink-0 font-urdu shadow-sm"
                    >
                      {copiedUrlToast ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span>{copiedUrlToast ? 'کاپی ہو گیا!' : 'خفیہ لنک کاپی کریں'}</span>
                    </button>
                  </div>

                  {/* Edit Slug Input */}
                  <div className="pt-2 border-t border-slate-700/60">
                    <label className="block text-xs font-bold text-slate-200 font-urdu mb-1.5">
                      خفیہ سلیگ تبدیل کریں (Custom Admin Slug):
                    </label>
                    <div className="flex items-center gap-2 max-w-md">
                      <span className="text-xs font-mono text-slate-400 bg-slate-950/60 px-3 py-2.5 rounded-xl border border-slate-700 dir-ltr">
                        domain.com/
                      </span>
                      <input
                        type="text"
                        value={settingsForm.adminSecretSlug || ''}
                        onChange={(e) => {
                          const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '');
                          setSettingsForm({ ...settingsForm, adminSecretSlug: sanitized });
                        }}
                        placeholder="sherazi-5083"
                        className="flex-1 p-2.5 rounded-xl border border-slate-600 bg-slate-900 text-white text-xs font-mono focus:border-emerald-500 focus:outline-none dir-ltr text-left"
                      />
                    </div>
                    <p className="text-[11px] text-amber-300/90 font-urdu mt-2 leading-relaxed">
                      ⚠️ اہم نوٹ: ہوم پیج، ہیڈر اور فوٹر سے لاگ ان بٹن مکمل طور پر ہٹا دیا گیا ہے۔ اس لنک کو اپنے براؤزر میں بک مارک (Bookmark) رکھیں تاکہ آپ آسانی سے لاگ ان ہو سکیں۔
                    </p>
                  </div>
                </div>

                {/* SECTION 2: ADMIN CREDENTIALS */}
                <div className="bg-slate-50 rounded-2xl p-5 sm:p-6 border border-slate-200 space-y-4">
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-sm font-urdu">
                    <KeyRound className="w-4 h-4 text-pansar-800" />
                    <span>ایڈمن لاگ اِن کریڈنشلز (Admin Username & Password)</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed font-urdu">
                    ایڈمن پینل کے لیے مضبوط یوزر نیم اور پاس ورڈ کا انتخاب کریں۔
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 font-urdu mb-1">
                        یوزر نیم (Username):
                      </label>
                      <input
                        type="text"
                        value={settingsForm.adminUsername || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, adminUsername: e.target.value })}
                        placeholder="admin"
                        className="w-full p-2.5 rounded-xl border border-gray-300 text-sm font-mono text-left dir-ltr bg-white focus:border-pansar-700 focus:outline-none shadow-2xs"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-[11px] font-bold text-gray-700 font-urdu mb-1">
                        نیا پاس ورڈ (New Password):
                      </label>
                      <input
                        type={showAdminPin ? 'text' : 'password'}
                        value={settingsForm.newAdminPassword || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, newAdminPassword: e.target.value })}
                        placeholder="تبدیل کرنے کے لیے نیا پاس ورڈ درج کریں"
                        className="w-full p-2.5 rounded-xl border border-gray-300 text-sm font-mono text-left dir-ltr bg-white focus:border-pansar-700 focus:outline-none shadow-2xs"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdminPin(!showAdminPin)}
                        className="absolute right-2.5 top-8 text-gray-400 hover:text-gray-600 p-1"
                        title={showAdminPin ? 'چھپائیں' : 'دیکھیں'}
                      >
                        {showAdminPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <span className="text-[10px] text-gray-500 font-urdu block mt-1">
                        * پاس ورڈ تبدیل نہ کرنا ہو تو اسے خالی رہنے دیں۔
                      </span>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: MULTI-FACTOR AUTHENTICATION (2FA) */}
                <div className="bg-emerald-50/50 rounded-2xl p-5 sm:p-6 border border-emerald-200 space-y-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-emerald-950 font-bold text-sm font-urdu">
                      <ShieldCheck className="w-5 h-5 text-emerald-700" />
                      <span>ج) ملٹی فیکٹر اتھنٹیکیشن (Two-Factor Authentication / 2FA)</span>
                    </div>
                    <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-200 text-emerald-900 font-urdu">
                      عالمی معیار کی سیکیورٹی
                    </span>
                  </div>

                  <p className="text-xs text-slate-700 leading-relaxed font-urdu">
                    صرف پاس ورڈ کے بجائے دوہری تصدیق (2FA) فعال کریں۔ پاس ورڈ دینے کے بعد آپ کے موبائل ایپ پر وقتی کوڈ یا ای میل پر OTP آئے گا تب لاگ ان مکمل ہوگا۔
                  </p>

                  {/* 2FA Mode Selection Radios */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    
                    {/* Option 1: Disabled */}
                    <label className={`relative p-4 rounded-xl border-2 transition cursor-pointer flex flex-col justify-between ${
                      (settingsForm.twoFactorType || 'disabled') === 'disabled'
                        ? 'border-slate-400 bg-white shadow-xs'
                        : 'border-gray-200 bg-white/70 hover:border-gray-300'
                    }`}>
                      <div className="flex items-start justify-between">
                        <span className="font-bold text-xs text-slate-800 font-urdu">بند رکھیں (Disabled)</span>
                        <input
                          type="radio"
                          name="twoFactorType"
                          value="disabled"
                          checked={(settingsForm.twoFactorType || 'disabled') === 'disabled'}
                          onChange={() => setSettingsForm(prev => ({ ...prev, twoFactorType: 'disabled' }))}
                          className="mt-0.5 text-slate-600 focus:ring-slate-500"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 font-urdu mt-2 leading-relaxed">
                        صرف یوزرنیم اور پاس ورڈ سے براہِ راست لاگ ان ہوگا۔
                      </p>
                    </label>

                    {/* Option 2: Email OTP */}
                    <label className={`relative p-4 rounded-xl border-2 transition cursor-pointer flex flex-col justify-between ${
                      settingsForm.twoFactorType === 'email_otp'
                        ? 'border-emerald-600 bg-emerald-50 shadow-xs'
                        : 'border-gray-200 bg-white/70 hover:border-gray-300'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-950 font-urdu">
                          <Mail className="w-3.5 h-3.5 text-emerald-700" />
                          <span>ای میل OTP کوڈ</span>
                        </div>
                        <input
                          type="radio"
                          name="twoFactorType"
                          value="email_otp"
                          checked={settingsForm.twoFactorType === 'email_otp'}
                          onChange={() => setSettingsForm(prev => ({ ...prev, twoFactorType: 'email_otp' }))}
                          className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                        />
                      </div>
                      <p className="text-[11px] text-emerald-800 font-urdu mt-2 leading-relaxed">
                        لاگ ان پر رجسٹرڈ ای میلز پر 6 ہندسوں کا تصدیقی OTP کوڈ بھیجا جائے گا۔
                      </p>
                    </label>

                    {/* Option 3: TOTP Authenticator App */}
                    <label className={`relative p-4 rounded-xl border-2 transition cursor-pointer flex flex-col justify-between ${
                      settingsForm.twoFactorType === 'totp'
                        ? 'border-emerald-700 bg-emerald-50 shadow-xs ring-1 ring-emerald-700'
                        : 'border-gray-200 bg-white/70 hover:border-gray-300'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-950 font-urdu">
                          <Smartphone className="w-3.5 h-3.5 text-emerald-700" />
                          <span>گوگل اتھینٹیکیٹر ایپ</span>
                        </div>
                        <input
                          type="radio"
                          name="twoFactorType"
                          value="totp"
                          checked={settingsForm.twoFactorType === 'totp'}
                          onChange={() => {
                            setSettingsForm(prev => ({ ...prev, twoFactorType: 'totp' }));
                            if (!totpSetupData) {
                              handleFetchTotpSetup(false);
                            }
                          }}
                          className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                        />
                      </div>
                      <p className="text-[11px] text-emerald-800 font-urdu mt-2 leading-relaxed">
                        Google / Microsoft Authenticator ایپ میں وقتی گھڑی والا کوڈ (TOTP)۔
                      </p>
                    </label>

                  </div>

                  {/* TOTP Setup Box (QR Code and Secret Key) */}
                  {settingsForm.twoFactorType === 'totp' && (
                    <div className="bg-white rounded-2xl p-5 border border-emerald-300 shadow-sm space-y-4 animate-fade-in">
                      <div className="flex items-center justify-between border-b border-emerald-100 pb-3">
                        <div className="flex items-center gap-2 font-bold text-xs text-emerald-950 font-urdu">
                          <QrCode className="w-4 h-4 text-emerald-700" />
                          <span>گوگل اتھینٹیکیٹر ایپ کنفیگریشن (Scan QR Code)</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleGenerateNewTotpSecret}
                          className="text-[11px] text-emerald-700 hover:text-emerald-900 flex items-center gap-1 font-urdu font-bold cursor-pointer bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          <span>نیا کیو آر کوڈ اور کی بنائیں</span>
                        </button>
                      </div>

                      <div className="flex flex-col md:flex-row items-center gap-6">
                        {/* QR Code Image */}
                        <div className="p-3 bg-white border border-gray-200 rounded-2xl shadow-xs shrink-0 text-center">
                          <img
                            src={
                              totpSetupData?.qr_url || 
                              totpSetupData?.qrUrl || 
                              `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                                `otpauth://totp/HimalayanPansar:${settingsForm.adminUsername || 'admin'}?secret=${settingsForm.totpSecret || totpSetupData?.totpSecret || 'KVKFK2TLNVWGS5DB'}&issuer=HimalayanPansar&period=30&digits=6`
                              )}`
                            }
                            alt="TOTP QR Code"
                            className="w-40 h-40 object-contain mx-auto"
                            onError={(e) => {
                              // Fallback QR service
                              const sec = settingsForm.totpSecret || 'KVKFK2TLNVWGS5DB';
                              const authStr = `otpauth://totp/HimalayanPansar:${settingsForm.adminUsername || 'admin'}?secret=${sec}&issuer=HimalayanPansar&period=30&digits=6`;
                              e.currentTarget.src = `https://quickchart.io/qr?text=${encodeURIComponent(authStr)}&size=220`;
                            }}
                          />
                          <span className="block text-[10px] text-gray-500 font-mono mt-1">
                            Google Authenticator
                          </span>
                        </div>

                        {/* Steps and Secret Key */}
                        <div className="flex-1 space-y-3 text-xs">
                          <ol className="list-decimal list-inside space-y-1.5 text-slate-700 font-urdu leading-relaxed">
                            <li>اپنے موبائل میں <strong>Google Authenticator</strong> یا <strong>Microsoft Authenticator</strong> ایپ کھولیں۔</li>
                            <li>ایپ میں پلس (+) پر کلک کریں اور <strong>"Scan a QR code"</strong> منتخب کر کے یہ کوڈ اسکین کریں۔</li>
                            <li>یا نیچے دی گئی <strong>سیکرٹ کی (Secret Key)</strong> کو ایپ میں مینوئل درج کریں۔</li>
                          </ol>

                          <div className="bg-slate-100 p-3 rounded-xl border border-slate-200 space-y-1">
                            <span className="text-[10px] text-slate-500 font-urdu font-bold block">
                              مینوئل سیکرٹ کی (Manual Secret Key):
                            </span>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono font-bold text-xs sm:text-sm text-slate-900 tracking-wider dir-ltr select-all">
                                {settingsForm.totpSecret || totpSetupData?.totpSecret || 'KVKFK2TLNVWGS5DB'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const sec = settingsForm.totpSecret || totpSetupData?.totpSecret || 'KVKFK2TLNVWGS5DB';
                                  navigator.clipboard.writeText(sec);
                                  alert('سیکرٹ کی کاپی ہو گئی!');
                                }}
                                className="px-2.5 py-1 rounded bg-white hover:bg-slate-200 text-slate-700 font-mono text-[11px] font-bold border border-slate-300 transition shrink-0"
                              >
                                کاپی کی
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 30-Day Trusted Devices Management */}
                  <div className="bg-white/80 rounded-xl p-4 border border-emerald-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-xs text-emerald-950 font-urdu">
                        تصدیق شدہ ڈیوائسز (30 Days Remembered Devices)
                      </h4>
                      <p className="text-[11px] text-emerald-800 font-urdu mt-0.5 leading-relaxed">
                        لاگ ان کے وقت "اس ڈیوائس کو 30 دن کے لیے یاد رکھیں" منتخب کرنے پر اس براؤزر میں 30 دن تک 2FA دوبارہ نہیں مانگا جائے گا۔
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleRevokeAllDevices}
                        disabled={revokingDevices}
                        className="px-3.5 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 hover:text-rose-900 border border-rose-200 font-bold text-xs font-urdu transition cursor-pointer flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>{revokingDevices ? 'منسوخ ہو رہا ہے...' : 'تمام ڈیوائسز کی توثیق ختم کریں'}</span>
                      </button>
                    </div>
                  </div>

                  {revokedMessage && (
                    <div className="p-3 bg-emerald-100 border border-emerald-300 text-emerald-950 rounded-xl text-xs font-urdu font-bold flex items-center gap-2 animate-fade-in">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                      <span>{revokedMessage}</span>
                    </div>
                  )}

                </div>

                {/* SECTION 4: MULTI-EMAIL RECOVERY & NOTIFICATIONS */}
                <div className="bg-slate-50 rounded-2xl p-5 sm:p-6 border border-slate-200 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-900 font-urdu mb-1 flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-pansar-700" />
                      <span>ایڈمن ریکوری و نوٹیفکیشن ای میلز (Admin Recovery & OTP Emails)</span>
                    </label>
                    <p className="text-[11px] text-gray-500 font-urdu leading-relaxed">
                      پاس ورڈ ری سیٹ اور 2FA ای میل تصدیقی کوڈ ان تمام رجسٹرڈ ای میلز پر ارسال کیے جائیں گے۔ آپ ایک یا ایک سے زیادہ ای میلز شامل کر سکتے ہیں۔
                    </p>
                  </div>

                  {/* Existing Emails List */}
                  <div className="flex flex-wrap gap-2">
                    {getAdminEmailList().map((em, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white px-3.5 py-2 rounded-xl border border-emerald-300 shadow-2xs text-xs font-mono text-emerald-950"
                      >
                        <span className="font-sans font-medium">{em}</span>
                        {getAdminEmailList().length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAdminEmail(em)}
                            className="p-1 rounded-full hover:bg-rose-100 text-rose-500 hover:text-rose-700 transition cursor-pointer"
                            title="ای میل ہٹائیں"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add New Email Input */}
                  <div className="flex flex-col sm:flex-row gap-2 max-w-lg pt-1">
                    <input
                      type="email"
                      value={newAdminEmailInput}
                      onChange={(e) => setNewAdminEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAdminEmail();
                        }
                      }}
                      placeholder="نئی ای میل ایڈریس (مثلاً: admin2@domain.com)"
                      className="flex-1 p-2.5 rounded-xl border border-gray-300 text-xs font-sans text-left dir-ltr bg-white focus:border-pansar-700 focus:outline-none shadow-2xs"
                    />
                    <button
                      type="button"
                      onClick={handleAddAdminEmail}
                      className="px-4 py-2.5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs font-urdu shadow transition cursor-pointer shrink-0"
                    >
                      + نئی ای میل شامل کریں
                    </button>
                  </div>
                </div>

                {/* Save Button Footer */}
                <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                  {settingsSaved && (
                    <span className="text-xs text-emerald-700 font-bold flex items-center gap-1 font-urdu bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4" />
                      سیٹنگز کامیابی سے محفوظ ہو گئیں!
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    className="py-3 px-8 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs shadow-md transition font-urdu cursor-pointer flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span>سیکیورٹی ترتیبات محفوظ کریں</span>
                  </button>
                </div>

              </div>
            </div>

          </div>
        )}

          </div>
        </main>
      </div>

      {/* Product Add/Edit Modal */}
      <ProductFormModal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSave={handleSaveProduct}
        editingProduct={editingProduct}
      />

      {/* Payment Receipt / Screenshot Preview Modal */}
      {viewingReceiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-gray-200 flex flex-col max-h-[90vh]">
            <div className="p-4 bg-emerald-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm font-urdu">ادائیگی کی رسید / اسکرین شاٹ</h3>
                <p className="text-xs text-emerald-200 font-sans mt-0.5">
                  آرڈر #{viewingReceiptModal.orderId} • {viewingReceiptModal.customerName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingReceiptModal(null)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition cursor-pointer font-bold text-base"
              >
                ✕
              </button>
            </div>

            <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs flex justify-between items-center px-4">
              <div>
                <span className="text-gray-500 font-urdu">ٹرانزیکشن ID: </span>
                <span className="font-mono font-bold text-gray-800 dir-ltr">{viewingReceiptModal.tid || 'درج نہیں'}</span>
              </div>
              <a
                href={viewingReceiptModal.img}
                download={`receipt-order-${viewingReceiptModal.orderId}.jpg`}
                className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-bold bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200 text-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>ڈاؤنلوڈ رسید</span>
              </a>
            </div>

            <div className="p-4 overflow-y-auto flex-1 flex items-center justify-center bg-gray-100 min-h-[250px]">
              <img
                src={viewingReceiptModal.img}
                alt="Payment Proof Receipt"
                className="max-w-full max-h-[55vh] object-contain rounded-lg shadow border border-gray-200"
              />
            </div>

            <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
              <button
                type="button"
                onClick={() => setViewingReceiptModal(null)}
                className="px-6 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-bold rounded-lg transition cursor-pointer"
              >
                بند کریں
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
